// ─── FuelOTPanel — gestão completa de uma OT fuel sem app móvel ──
//
// Renderizado dentro do detail expandido da OT quando serviceType==='fuel'.
// Concentra TODOS os marcos + popups num único componente isolado, para
// não tocar no resto do detail panel (assistance OTs ficam intactas).
//
// Marcos:
//   [Saí da base] [Cheguei] [Carregou] [Descarregou] [Concluir] [Regressei à base]
//
// Cada botão fica disabled quando a precondição não é cumprida — operador
// não pode "Carregou" antes de "Cheguei", etc. Marcos visuais com timeline.
import React, { useState, useContext } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Menu, MenuItem } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CloseIcon from '@material-ui/icons/Close';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import StarIcon from '@material-ui/icons/Star';
// Ícones temáticos para a cronologia (cada marco tem o seu).
import DirectionsRunIcon from '@material-ui/icons/DirectionsRun';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import PinDropIcon from '@material-ui/icons/PinDrop';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import HomeWorkIcon from '@material-ui/icons/HomeWork';
import { AuthContext } from '../../App';
import AddressFieldShared from './AddressFieldShared';

function fmtTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// Distância em km entre dois pontos (haversine). Devolve Infinity se faltar algum valor.
function kmBetween(aLat, aLng, bLat, bLng) {
  const a1 = Number(aLat), a2 = Number(aLng), b1 = Number(bLat), b2 = Number(bLng);
  if (![a1, a2, b1, b2].every(Number.isFinite)) return Infinity;
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b1 - a1), dLng = toRad(b2 - a2);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a1)) * Math.cos(toRad(b1)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
// Raio da "zona" do P.A. — igual ao auto-marcador por GPS (FUEL_AUTO_RADIUS_KM no backend).
const PA_ZONE_KM = 0.2; // 200 m

function FuelOTPanel({ order, onUpdated, onReplan, onStartRoute, onInteractingChange, vehiclePos = null, lang = 'pt' }) {
  // Helper de tradução curto.
  const T = (pt, en) => (lang === 'pt' ? pt : en);
  const { token, userPermission, userRole } = useContext(AuthContext);
  const [busy, setBusy] = useState(null); // string com a action a decorrer (loading state)
  const [dialog, setDialog] = useState(null); // 'loaded' | 'unloading' | 'completed' | null
  const [confirmOverridePa, setConfirmOverridePa] = useState(null); // 'pa1' | 'pa2' | null — override do gestor
  const [paForm, setPaForm] = useState(null); // Ponto 4 — dialog "Alterar P.A." ({name,address,lat,lng} | null)
  const [splitForm, setSplitForm] = useState(null); // Fase B — dialog "Entrega dividida (2 P.A.)"
  // Menu de favoritos do diálogo de entrega dividida (2.º P.A.).
  const [splitFavAnchor, setSplitFavAnchor] = useState(null);
  const [splitFavQuery, setSplitFavQuery] = useState('');
  const [splitFavDir, setSplitFavDir] = useState('asc');
  const closeSplitFav = () => { setSplitFavAnchor(null); setSplitFavQuery(''); };
  // Menu de favoritos do diálogo "Alterar P.A. de destino".
  const [paFavAnchor, setPaFavAnchor] = useState(null);
  const [paFavQuery, setPaFavQuery] = useState('');
  const [paFavDir, setPaFavDir] = useState('asc');
  const closePaFav = () => { setPaFavAnchor(null); setPaFavQuery(''); };
  const [error, setError] = useState(null);
  // Favoritos (ClientLocation) para o picker do PA no popup "Carregou".
  const [favorites, setFavorites] = useState([]);
  // Dialog profissional "PA revelado, recalcular rota?" — substitui alert().
  const [replanPrompt, setReplanPrompt] = useState(null);
  // Dialog "Detalhes do marco" — abre quando o operador clica numa bola feita.
  const [markDetails, setMarkDetails] = useState(null); // { kind, label, ... }
  // Métricas reais por troço (km GPS + tempo) + tipo de partida (base/veículo).
  const [legMetrics, setLegMetrics] = useState(null);
  // Fotos das guias (base64 pesado) — já NÃO vêm na lista de OT (poupa ~20 MB
  // por página). Busca-se on-demand quando o painel abre e a OT tem foto
  // (flags hasLoadingPhoto/hasDeliveryPhoto). Ver GET /:id.
  const [photos, setPhotos] = useState(null);
  // Ampliação da foto da guia (lightbox com zoom), igual ao FuelDeliveryReport.
  const [zoom, setZoom] = useState(null); // { url, label } | null
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const closeZoom = () => { setZoom(null); setZoomScale(1); };

  const report = order.assistanceReport || {};

  // Avisa o ecrã de Ordens quando há um popup aberto, para ele PAUSAR o
  // auto-refresh (senão a atualização de posições fecha o popup a meio).
  React.useEffect(() => {
    const interacting = !!(dialog || markDetails || replanPrompt || zoom);
    if (onInteractingChange) onInteractingChange(interacting);
    return () => { if (onInteractingChange) onInteractingChange(false); };
  }, [dialog, markDetails, replanPrompt, zoom, onInteractingChange]);

  // Cliente final da OT (Sonangol / Total / Pumangol / Sonangalp) — para o
  // título do popup de carregamento e outros labels. Usa order.fuelCustomer;
  // fallback ao prefixo do customRef (OTs antigas sem campo); default Sonangol.
  const fuelCustomerLabel = (() => {
    const map = { sonangol: 'Sonangol', total: 'Total', pumangol: 'Pumangol', sonangalp: 'Sonangalp' };
    if (order.fuelCustomer && map[order.fuelCustomer]) return map[order.fuelCustomer];
    const ref = order.customRef || '';
    if (ref.startsWith('Tot-')) return 'Total';
    if (ref.startsWith('Pum-')) return 'Pumangol';
    if (ref.startsWith('Snp-')) return 'Sonangalp';
    return 'Sonangol';
  })();

  // Métricas reais (km GPS por troço + tempo + tipo de partida). Busca-se
  // quando há pelo menos "Saída da base" e re-busca quando um marco muda.
  React.useEffect(() => {
    if (!token || !order?.id) return;
    if (!report.departedBaseAt) { setLegMetrics(null); return; }
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`/planner-api/transport-orders/${order.id}/fuel-leg-metrics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancel) setLegMetrics(json);
      } catch (_) { /* silencioso */ }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id, report.departedBaseAt, report.arrivedAt, report.arrivedPaAt, report.completedAt, report.returnedBaseAt, token]);

  // Hidrata as fotos das guias on-demand (só quando o painel abre e a OT tem
  // foto). A lista já não as traz para poupar largura de banda; aqui pesa 1
  // fetch por OT expandida, o que é irrelevante.
  React.useEffect(() => {
    if (!token || !order?.id) return undefined;
    if (!order.hasLoadingPhoto && !order.hasDeliveryPhoto) { setPhotos(null); return undefined; }
    if (order.loadingPhotoUrl || order.deliveryPhotoUrl) return undefined; // já vieram inline
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`/planner-api/transport-orders/${order.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancel) setPhotos({ loadingPhotoUrl: json.loadingPhotoUrl || null, deliveryPhotoUrl: json.deliveryPhotoUrl || null });
      } catch (_) { /* silencioso */ }
    })();
    return () => { cancel = true; };
  }, [order.id, order.hasLoadingPhoto, order.hasDeliveryPhoto, order.loadingPhotoUrl, order.deliveryPhotoUrl, token]);

  // URLs efetivos das fotos (hidratadas > inline). Usar sempre estes na UI.
  const loadingPhotoUrl = photos?.loadingPhotoUrl || order.loadingPhotoUrl || null;
  const deliveryPhotoUrl = photos?.deliveryPhotoUrl || order.deliveryPhotoUrl || null;

  // Carrega favoritos quando algum diálogo que os usa está aberto: popup
  // "Carregou" (REVELAR PA REAL), "Entrega dividida" (2.º P.A.) e "Alterar
  // P.A. de destino". A guarda `favorites.length > 0` evita voltar a buscar
  // (o splitForm/paForm mudam a cada tecla, mas o fetch só corre 1x).
  React.useEffect(() => {
    const needFavs = dialog === 'loaded' || !!splitForm || !!paForm;
    if (!needFavs || !token || favorites.length > 0) return;
    let cancel = false;
    (async () => {
      try {
        const res = await fetch('/planner-api/locations', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const json = await res.json();
        // Ordenar por ordem alfabética (nome, senão morada) — facilita a pesquisa.
        const arr = Array.isArray(json) ? json : (json.locations || []);
        const sorted = [...arr].sort((a, b) =>
          String(a.name || a.address || '').localeCompare(String(b.name || b.address || ''), 'pt', { sensitivity: 'base' }));
        if (!cancel) setFavorites(sorted);
      } catch (_) { /* silencioso */ }
    })();
    return () => { cancel = true; };
  }, [dialog, splitForm, paForm, token, favorites.length]);

  // Pré-condições de cada marco — gating intuitivo:
  //   departed_base : pode sempre (1º passo)
  //   arrived       : precisa de departed_base
  //   loaded        : precisa de arrived
  //   arrived_pa    : precisa de loaded (chegou ao PA, pode ter de esperar/fila)
  //   unloading     : precisa de arrived_pa
  //   completed     : precisa de unloading
  //   returned_base : precisa de completed
  const hasDeparted = !!report.departedBaseAt;
  const hasArrived = !!report.arrivedAt;
  // Início do carregamento (opcional) — separa a ESPERA na fila do terminal
  // (arrivedAt → loadingStartedAt) do tempo de carregamento.
  const hasLoadingStarted = !!report.loadingStartedAt;
  // hasLoaded SÓ baseado em loadingOrderRef + foto — o operador clicou
  // "Carregou" e registou a Ordem Sonangol. NÃO usar loadingOrderQty
  // porque pode estar populated com expectativa (ou null) — não é fiável.
  const hasLoaded = !!order.loadingOrderRef || !!order.loadingPhotoUrl || !!order.hasLoadingPhoto;
  const hasDepartedTerminal = !!report.departedTerminalAt; // saída do terminal (opcional)
  const hasArrivedPa = !!report.arrivedPaAt; // chegada ao PA (ponto intermédio)
  const hasDepartedPa = !!report.departedPaAt; // saída do PA (auto-GPS; alerta na listagem)
  // Presença na ZONA do P.A. (pedido Move Express): a "Chegada ao P.A." só
  // desbloqueia quando a viatura está dentro de 200 m do P.A. (a mesma zona do
  // auto-marcador). `atPaN`: true = dentro, false = fora (bloqueia), null =
  // desconhecido (sem GPS ou P.A. sem coordenadas) → NÃO bloqueia (fallback,
  // senão uma falha de GPS prendia o serviço).
  const presenceAtPa = (lat, lng) => {
    const hasCoords = lat != null && lng != null;
    if (!vehiclePos || vehiclePos.lat == null || vehiclePos.lng == null || !hasCoords) return null;
    return kmBetween(vehiclePos.lat, vehiclePos.lng, lat, lng) <= PA_ZONE_KM;
  };
  const atPa1 = presenceAtPa(order.deliveryLat, order.deliveryLng);
  const atPa2 = presenceAtPa(order.deliveryLat2, order.deliveryLng2);
  // Override do GESTOR (pedido Move Express): o gestor (ou super admin) pode
  // validar a "Chegada ao P.A." mesmo fora da zona do P.A. definido como
  // destino — com confirmação. Operador/viewer continuam bloqueados fora da
  // zona. NOTA: o default de permissão é 'manager' (users sem papel atribuído
  // ficam gestores); quando existirem operadores definidos, ficam gated.
  const isManager = userPermission === 'manager' || userRole === 'super_admin';
  // A cronologia é a EXECUÇÃO da viagem — só faz sentido marcar marcos depois de
  // a OT estar DESPACHADA (tem rota atribuída). Antes disso (por atribuir / sem
  // rota) nada se marca, nem sequer o gestor (o override do P.A. não abre a
  // cronologia inteira). Fecha o furo de marcar numa OT ainda por atribuir.
  const canExecute = !!order.assignedRouteId;
  // Nomes de local para enriquecer a cronologia (marco Chegada ao terminal e Fim
  // de viagem) — o operador vê ONDE sem abrir o detalhe.
  const terminalName = (order.pickupName || '').trim() || null;
  const baseName = (order.routeOrigin?.name || order.routeOrigin?.text || order.routeOrigin?.address || '').trim() || null;
  // Fim de viagem: se segue para o terminal, o terminal; senão, a base de regresso.
  const tripEndName = (order.nextDestination === 'terminal')
    ? (terminalName || T('Terminal', 'Terminal'))
    : (baseName || T('Base', 'Base'));
  const hasUnloaded = !!order.deliveryGuideRef || !!order.deliveryPhotoUrl || !!order.hasDeliveryPhoto;
  const hasCompleted = !!report.completedAt;
  const hasReturned = !!report.returnedBaseAt;
  // Ponto 2 — próximo destino após a entrega ('terminal' = segue direto, não
  // regressa à base).
  const goTerminal = order.nextDestination === 'terminal';

  // ── Fase B — entrega dividida por 2 P.A. ──────────────────────────────
  // splitDelivery=true → a cisterna descarrega em 2 postos. A 1.ª descarga usa
  // os marcos normais; o 2.º posto acrescenta "Chegada P.A.2" + "Entrega P.A.2"
  // entre a 1.ª descarga e a conclusão. Só a Move Express usa isto.
  const isSplit = !!order.splitDelivery;
  // #8 — a entrega dividida só faz sentido quando o 1.º P.A. já está totalmente
  // preenchido (nome + GPS). Em OT "PA por confirmar" o P.A. real só é revelado
  // no "Carregou", por isso o botão de dividir fica bloqueado até lá.
  const pa1Ready = !!(String(order.deliveryName || '').trim()
    && order.deliveryLat != null && order.deliveryLng != null);
  const hasArrivedPa2 = !!report.arrivedPa2At;
  const hasUnloaded2 = !!order.deliveryGuideRef2 || !!order.deliveryPhotoUrl2 || !!order.hasDeliveryPhoto2;
  // Concluir exige a 2.ª descarga quando a OT é dividida.
  const canComplete = isSplit ? hasUnloaded2 : hasUnloaded;

  // Fase B — grava a configuração da divisão (2.º P.A. + litros por posto).
  async function saveSplit(form) {
    setError(null);
    try {
      const body = {
        splitDelivery: true,
        deliveryName2: form.deliveryName2 || null,
        deliveryAddress2: form.deliveryAddress2 || null,
        deliveryLat2: form.deliveryLat2 != null ? form.deliveryLat2 : null,
        deliveryLng2: form.deliveryLng2 != null ? form.deliveryLng2 : null,
        plannedQty1: form.plannedQty1 !== '' && form.plannedQty1 != null ? Number(form.plannedQty1) : null,
        plannedQty2: form.plannedQty2 !== '' && form.plannedQty2 != null ? Number(form.plannedQty2) : null,
      };
      const r = await fetch(`/planner-api/transport-orders/${order.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error('erro');
      setSplitForm(null);
      if (onUpdated) onUpdated();
      // Se a OT já tem rota, a rota não passa pelo 2.º P.A. — oferecer recálculo.
      if (order.assignedRouteId) {
        setReplanPrompt({
          split: true,
          paName: form.deliveryName2 || T('2.º P.A.', '2nd point'),
          paAddress: form.deliveryAddress2 || null,
          routeId: order.assignedRouteId,
        });
      }
    } catch (e) { setError(T('Não consegui guardar a entrega dividida.', 'Could not save the split delivery.')); }
  }

  // Fase B — desactiva a divisão (volta a 1 P.A.).
  async function clearSplit() {
    setError(null);
    try {
      await fetch(`/planner-api/transport-orders/${order.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ splitDelivery: false }),
      });
      setSplitForm(null);
      if (onUpdated) onUpdated();
    } catch (e) { setError(T('Não consegui alterar.', 'Could not update.')); }
  }

  // Ponto 2 — grava o próximo destino (base/terminal).
  async function changeNextDest(dest) {
    try {
      await fetch(`/planner-api/transport-orders/${order.id}/next-destination`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ nextDestination: dest }),
      });
      if (onUpdated) onUpdated();
    } catch { /* silencioso */ }
  }

  // Ponto 4 — altera o P.A. de destino (motorista redirecionado). Regista a
  // alteração no backend. `pa` = { deliveryName, deliveryAddress, deliveryLat, deliveryLng }.
  async function changePA(pa) {
    try {
      const r = await fetch(`/planner-api/transport-orders/${order.id}/change-pa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(pa),
      });
      if (!r.ok) throw new Error('erro');
      setPaForm(null); // o diálogo "Alterar P.A." é controlado por paForm, não por dialog
      if (onUpdated) onUpdated();
    } catch (e) { setError(T('Não consegui alterar o P.A.', 'Could not change delivery point')); }
  }

  async function postEvent(action, body) {
    setBusy(action);
    setError(null);
    try {
      const r = await fetch(`/planner-api/transport-orders/${order.id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body || {}),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erro a gravar marco');
      // Aviso quando o "Carregou" revela um PA real e a rota já estava
      // calculada — o destino mudou, é preciso re-planificar. Dialog
      // profissional em vez de alert nativo do browser.
      if (
        action === 'loaded'
        && body?.deliveryName
        && order.assignedRouteId
      ) {
        // Piloto automático (default ON): calcula + grava a rota e volta à
        // cronologia sozinho. Se desligado, mostra o prompt de recálculo manual.
        const autopilot = body?._autopilot !== false;
        if (autopilot && typeof onReplan === 'function') {
          onReplan(order.assignedRouteId, { fuelRebuild: true, autoSaveReturn: true });
        } else {
          setReplanPrompt({
            paName: body.deliveryName,
            paAddress: body.deliveryAddress,
            routeId: order.assignedRouteId,
          });
        }
      }
      if (onUpdated) onUpdated();
      setDialog(null);
    } catch (e) {
      setError(e.message);
    }
    setBusy(null);
  }

  // Helper: duração entre 2 ISO timestamps formatada como "1h 5m" / "23min"
  function fmtDuration(fromIso, toIso) {
    if (!fromIso || !toIso) return null;
    const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    const mins = Math.round(ms / 60000);
    if (mins < 1) return '<1min';
    if (mins < 60) return `${mins}min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}min`;
  }

  // Formata km de um troço (das métricas GPS reais). Null → sem etiqueta.
  function fmtKm(km) {
    if (km == null) return null;
    return `${Number(km).toLocaleString('pt-PT', { maximumFractionDigits: 1 })} km`;
  }
  const legKm = (key) => fmtKm(legMetrics?.legs?.[key]?.km);
  // Origem REAL da partida — a 1ª bola deve dizer de ONDE o veículo saiu:
  //  · veículo  → recalculou a rota a partir da posição GPS actual
  //  · terminal → a origem da rota coincide com o ponto de recolha (terminal)
  //  · base     → parque/base do cliente (default)
  // Deriva de order.routeOrigin (vem na lista) + pickup, por isso funciona
  // ANTES de arrancar (legMetrics só existe depois da saída).
  const departureKind = (() => {
    const o = order.routeOrigin || legMetrics?.origin || null;
    const oName = o?.text || o?.name || o?.address || legMetrics?.departureName || '';
    if (legMetrics?.departureIsVehicle
      || /posi[çc][ãa]o actual do ve[íi]culo|local atual do ve[íi]culo|🚗/i.test(String(oName))) return 'vehicle';
    const oc = o?.coordinates;
    const oLng = Array.isArray(oc) ? Number(oc[0]) : Number(o?.lng ?? o?.longitude);
    const oLat = Array.isArray(oc) ? Number(oc[1]) : Number(o?.lat ?? o?.latitude);
    const pLat = order.pickupLat != null ? Number(order.pickupLat) : null;
    const pLng = order.pickupLng != null ? Number(order.pickupLng) : null;
    if ([oLat, oLng, pLat, pLng].every(Number.isFinite)
      && Math.abs(oLat - pLat) < 0.0015 && Math.abs(oLng - pLng) < 0.0015) return 'terminal';
    return 'base';
  })();
  // Nome da 1ª bola — dinâmico consoante a partida real.
  const departureLabelPt = departureKind === 'vehicle' ? 'Saída local do veículo'
    : departureKind === 'terminal' ? 'Saída do terminal' : 'Saída da base';
  const departureLabelEn = departureKind === 'vehicle' ? 'Left vehicle location'
    : departureKind === 'terminal' ? 'Left terminal' : 'Left base';

  // ── Renderização de cada marco (timeline horizontal) ──────────────
  // 3 estados: done (clicável para ver detalhes), next (clicável para
  // executar, com pulse), disabled (não clicável).
  const Step = ({ id, label, icon, done, ts, disabled, onClick, onShowDetails, subLabel }) => {
    const isNext = !done && !disabled;
    const isClickable = isNext || done;
    return (
      <Box style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        flex: '1 1 0', minWidth: 88, position: 'relative',
      }}>
        {/* Anel exterior gradient — só nas bolas activas (done ou next).
            Cria sensação de profundidade e destaca do background. */}
        <Box style={{
          width: 52, height: 52, borderRadius: '50%',
          background: done
            ? 'conic-gradient(from 180deg, #16a34a, #4ade80, #16a34a, #15803d)'
            : isNext
              ? 'conic-gradient(from 180deg, #fb923c, #fdba74, #f97316, #ea580c)'
              : 'transparent',
          padding: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: disabled ? 0.4 : 1,
          animation: isNext ? 'fuelStepRingSpin 4s linear infinite' : 'none',
        }}>
          <Box
            onClick={isClickable ? (done && onShowDetails ? onShowDetails : (isNext ? onClick : undefined)) : undefined}
            style={{
              width: 46, height: 46, borderRadius: '50%',
              background: done
                ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 60%, #15803d 100%)'
                : disabled
                  ? 'linear-gradient(135deg, #f1f5f9, #e2e8f0)'
                  : 'linear-gradient(135deg, #ffedd5 0%, #fed7aa 60%, #fdba74 100%)',
              border: `2px solid ${done ? '#fff' : disabled ? '#cbd5e1' : '#fff'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: isClickable ? 'pointer' : 'default',
              boxShadow: done
                ? 'inset 0 -3px 6px rgba(0,0,0,0.18), 0 4px 14px rgba(22,163,74,0.40)'
                : (isNext
                    ? 'inset 0 -3px 6px rgba(154,52,18,0.22), 0 4px 16px rgba(249,115,22,0.38)'
                    : 'inset 0 -2px 4px rgba(15,23,42,0.06)'),
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              opacity: busy === id ? 0.55 : 1,
              animation: isNext ? 'fuelStepPulse 2s ease-in-out infinite' : 'none',
              position: 'relative',
            }}
            onMouseEnter={(e) => { if (isClickable) { e.currentTarget.style.transform = 'scale(1.10)'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {/* Ícone temático sempre visível. Cor adapta-se ao estado. */}
            {React.cloneElement(icon, {
              style: {
                color: done ? '#fff' : disabled ? '#cbd5e1' : '#9a3412',
                fontSize: 23,
                filter: done ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.18))' : 'none',
              },
            })}
            {/* Badge ✓ no canto quando feito — circulo branco com tick verde */}
            {done && (
              <Box style={{
                position: 'absolute',
                right: -2, bottom: -2,
                width: 18, height: 18, borderRadius: '50%',
                background: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(15,23,42,0.20)',
                border: '1.5px solid #fff',
              }}>
                <CheckCircleIcon style={{ color: '#16a34a', fontSize: 15 }} />
              </Box>
            )}
            {/* Cadeado pequeno quando disabled — sinaliza que está bloqueado */}
            {disabled && (
              <Box style={{
                position: 'absolute',
                right: -2, bottom: -2,
                width: 17, height: 17, borderRadius: '50%',
                background: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(15,23,42,0.10)',
                border: '1.5px solid #e2e8f0',
                fontSize: 10,
                color: '#94a3b8',
              }}>🔒</Box>
            )}
          </Box>
        </Box>
        <Typography style={{
          fontSize: '0.62rem', fontWeight: 800, marginTop: 6,
          color: done ? '#15803d' : disabled ? '#94a3b8' : '#9a3412',
          textAlign: 'center', lineHeight: 1.15,
          textTransform: 'uppercase', letterSpacing: 0.3,
        }}>
          {label}
        </Typography>
        {ts ? (
          <Typography style={{
            fontSize: '0.64rem', color: '#15803d', marginTop: 3,
            fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700,
          }}>
            {fmtTime(ts)}
          </Typography>
        ) : (
          <Typography style={{
            fontSize: '0.6rem', color: '#cbd5e1', marginTop: 3,
            fontFamily: 'ui-monospace, Menlo, monospace',
          }}>
            —
          </Typography>
        )}
        {/* Local do marco (ex.: terminal de carga, base de regresso) — enriquece
            a cronologia para o operador ver ONDE sem ter de abrir o detalhe. */}
        {subLabel && (
          <Typography title={subLabel} style={{
            fontSize: '0.58rem', fontWeight: 700, color: '#475569', marginTop: 2,
            textAlign: 'center', lineHeight: 1.2, maxWidth: 96,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            📍 {subLabel}
          </Typography>
        )}
        {/* Indicador "clica para ver" nas bolas feitas — texto subtil */}
        {done && onShowDetails && (
          <Typography style={{
            fontSize: '0.56rem', color: '#94a3b8', marginTop: 2,
            fontStyle: 'italic', textAlign: 'center',
          }}>
            {T('clica para ver', 'click to view')}
          </Typography>
        )}
      </Box>
    );
  };

  // Conector com tempo decorrido entre marcos (quando ambos têm timestamp).
  // Aparece como pílula no meio da linha — operador vê em relance quanto
  // tempo entre Saí → Cheguei, Cheguei → Carregou, etc.
  const Connector = ({ done, durationLabel, distanceLabel }) => (
    <Box style={{
      flex: '1 1 28px', minWidth: 28,
      position: 'relative', marginTop: 20,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Box style={{
        height: 3, width: '100%',
        background: done ? 'linear-gradient(90deg, #15803d, #16a34a)' : '#e2e8f0',
        borderRadius: 2,
      }} />
      {(durationLabel || distanceLabel) && (
        <Box style={{
          position: 'absolute',
          background: '#fff', border: '1px solid #bbf7d0',
          fontSize: '0.56rem', fontWeight: 800,
          padding: '2px 8px', borderRadius: 8,
          fontFamily: 'ui-monospace, Menlo, monospace',
          letterSpacing: '-0.01em',
          boxShadow: '0 1px 3px rgba(22,163,74,0.18)',
          whiteSpace: 'nowrap',
          display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.3,
        }}>
          {durationLabel && <span style={{ color: '#15803d' }}>{durationLabel}</span>}
          {distanceLabel && <span style={{ color: '#1e40af' }}>{distanceLabel}</span>}
        </Box>
      )}
    </Box>
  );

  // Divergência calculada
  const loadedQty = order.loadingOrderQty != null ? Number(order.loadingOrderQty) : null;
  const unloadQty = order.deliveryGuideQty != null ? Number(order.deliveryGuideQty) : null;
  // Fase B — 2.ª descarga; total descarregado = P.A.1 + P.A.2 (quando dividida).
  const unloadQty2 = order.deliveryGuideQty2 != null ? Number(order.deliveryGuideQty2) : null;
  const totalUnloaded = isSplit
    ? ((unloadQty || 0) + (unloadQty2 || 0)) || null
    : unloadQty;
  const divergence = (loadedQty != null && totalUnloaded != null) ? (loadedQty - totalUnloaded) : null;

  return (
    <Box style={{
      padding: 14, margin: '8px 0',
      background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 70%)',
      border: '1px solid #fed7aa', borderRadius: 12,
    }}>
      <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Typography style={{ fontSize: '0.78rem', fontWeight: 800, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          ⛽ {T('Cronologia da distribuição', 'Distribution timeline')}
        </Typography>
        {(() => {
          // Lista de cisternas atreladas: usa o array `cisterns` (até 2) e cai
          // para o campo singular antigo quando não existe array.
          const list = Array.isArray(order.cisterns) && order.cisterns.length
            ? order.cisterns
            : (order.cisternPlate ? [{ plate: order.cisternPlate, capacityM3: order.cisternCapacityM3 }] : []);
          if (!list.length) return null;
          const total = list.reduce((s, c) => s + (Number(c.capacityM3) || 0), 0);
          return (
            <Typography style={{ fontSize: '0.7rem', color: '#475569', fontFamily: 'ui-monospace, Menlo, monospace' }}>
              {T(list.length > 1 ? 'Cisternas' : 'Cisterna', list.length > 1 ? 'Cisterns' : 'Cistern')}:{' '}
              {list.map((c, i) => (
                <span key={i}>
                  {i > 0 && <span style={{ color: '#cbd5e1' }}> + </span>}
                  <strong>{c.plate}</strong>{c.capacityM3 != null && ` (${c.capacityM3} m³)`}
                </span>
              ))}
              {list.length > 1 && <span style={{ color: '#15803d', fontWeight: 800 }}> · Σ {total} m³</span>}
            </Typography>
          );
        })()}
      </Box>

      {/* Selo de ENTREGA DIVIDIDA — visível assim que o 2.º P.A. é gravado,
          mesmo antes de a viagem lá chegar. Mostra os 2 postos + litros/posto. */}
      {isSplit && (
        <Box style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          marginBottom: 12, padding: '8px 12px', borderRadius: 10,
          background: '#fffbeb', border: '1px solid #fde68a',
        }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#92400e' }}>
            🔀 {T('Entrega dividida por 2 P.A.', 'Split delivery — 2 points')}
          </span>
          <span style={{ fontSize: '0.7rem', color: '#475569', fontFamily: 'ui-monospace, Menlo, monospace' }}>
            <strong>P.A. 1</strong>{order.deliveryName ? ` · ${order.deliveryName}` : ''}
            {order.plannedQty1 != null ? ` · ${Number(order.plannedQty1).toLocaleString('pt-PT')} L` : ''}
          </span>
          <span style={{ color: '#cbd5e1' }}>→</span>
          <span style={{ fontSize: '0.7rem', color: '#475569', fontFamily: 'ui-monospace, Menlo, monospace' }}>
            <strong>P.A. 2</strong>{order.deliveryName2 ? ` · ${order.deliveryName2}` : ''}
            {order.plannedQty2 != null ? ` · ${Number(order.plannedQty2).toLocaleString('pt-PT')} L` : ''}
          </span>
        </Box>
      )}

      {/* Animações CSS: pulse na bola próxima + rotação suave do anel. */}
      <style>{`
        @keyframes fuelStepPulse {
          0%, 100% { box-shadow: inset 0 -3px 6px rgba(154,52,18,0.22), 0 4px 16px rgba(249,115,22,0.38); }
          50% { box-shadow: inset 0 -3px 6px rgba(154,52,18,0.22), 0 4px 22px rgba(249,115,22,0.62), 0 0 0 5px rgba(249,115,22,0.10); }
        }
        @keyframes fuelStepRingSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Timeline horizontal com ícones temáticos + duração entre marcos.
          Click em bola feita abre dialog "Detalhes do marco" com os dados
          que foram gravados. */}
      {!canExecute && (
        <Box style={{ margin: '0 4px 10px', padding: '9px 13px', borderRadius: 10, background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', fontSize: '0.72rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          🔒 {T('Despache a OT (veículo + rota) para iniciar a cronologia.', 'Dispatch the order (vehicle + route) to start the timeline.')}
        </Box>
      )}
      <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 0, padding: '4px 4px 0', overflowX: 'auto' }}>
        <Step id="departed-base" label={T('Início de viagem', 'Trip start')}
          icon={<DirectionsRunIcon />}
          done={hasDeparted} ts={report.departedBaseAt}
          disabled={!canExecute}
          // "Início de viagem" = iniciar a rota. Abre o diálogo de início (rota
          // planeada base→terminal→cliente). Iniciar a rota auto-marca este
          // marco no backend. Sem rota atribuída, fallback ao marco manual.
          onClick={() => (order.assignedRouteId && onStartRoute) ? onStartRoute() : postEvent('departed-base')}
          onShowDetails={() => setMarkDetails({ kind: 'departed', label: T('Início de viagem', 'Trip start'), ts: report.departedBaseAt })} />
        <Connector done={hasDeparted}
          durationLabel={fmtDuration(report.departedBaseAt, report.arrivedAt)}
          distanceLabel={legKm('base_terminal')} />
        <Step id="arrived" label={T('Chegada ao terminal de abastecimento', 'Arrived at loading terminal')}
          icon={<LocationOnIcon />}
          done={hasArrived} ts={report.arrivedAt}
          subLabel={terminalName}
          disabled={!hasDeparted} onClick={() => postEvent('arrived')}
          onShowDetails={() => setMarkDetails({ kind: 'arrived', label: T('Chegada ao terminal de abastecimento', 'Arrived at loading terminal'), ts: report.arrivedAt })} />
        {/* Passo OPCIONAL: início do carregamento (depois de eventual espera na
            fila). Como é opcional, considera-se IMPLICITAMENTE feito (verde) se o
            carregamento já foi efectuado ou em OTs antigas — nesse caso espera=0. */}
        {(() => {
          const startedDone = hasLoadingStarted || hasLoaded;
          const waitLabel = hasLoadingStarted
            ? fmtDuration(report.arrivedAt, report.loadingStartedAt)
            : (hasLoaded ? '0min' : undefined);
          return (
            <>
              {/* Conector com o TEMPO DE ESPERA na fila (chegada → início). */}
              <Connector done={startedDone} durationLabel={waitLabel} />
              <Step id="loading-started" label={T('Início de carregamento', 'Loading started')}
                icon={<LocalGasStationIcon />}
                done={startedDone} ts={report.loadingStartedAt}
                disabled={!hasArrived} onClick={() => postEvent('loading-started')}
                onShowDetails={() => setMarkDetails({ kind: 'loading-started', label: T('Início de carregamento', 'Loading started'), ts: report.loadingStartedAt })} />
            </>
          );
        })()}
        <Connector done={hasLoaded} />
        <Step id="loaded" label={T('Fim de carregamento', 'Loading finished')}
          icon={<LocalGasStationIcon />}
          done={hasLoaded} ts={null}
          disabled={!hasArrived} onClick={() => setDialog('loaded')}
          onShowDetails={() => setMarkDetails({ kind: 'loaded', label: T('Fim de carregamento', 'Loading finished') })} />
        {/* NOVO marco (opcional): Saída do terminal de abastecimento — separa o
            tempo de carregamento do tempo de espera no terminal. */}
        <Connector done={hasDepartedTerminal} />
        <Step id="departed-terminal" label={T('Saída do terminal de abastecimento', 'Left loading terminal')}
          icon={<ExitToAppIcon />}
          done={hasDepartedTerminal} ts={report.departedTerminalAt}
          disabled={!hasLoaded} onClick={() => postEvent('departed-terminal')}
          onShowDetails={() => setMarkDetails({ kind: 'departed-terminal', label: T('Saída do terminal de abastecimento', 'Left loading terminal'), ts: report.departedTerminalAt })} />
        <Connector done={hasArrivedPa}
          durationLabel={fmtDuration(report.departedTerminalAt || report.arrivedAt, report.arrivedPaAt)}
          distanceLabel={legKm('terminal_pa')} />
        {/* Bloco dos P.A. — 1 linha (entrega única) ou 2 linhas ALINHADAS na
            mesma coluna (entrega dividida): P.A.1 em cima, P.A.2 por baixo.
            Assim "Chegada P.A.2" fica exactamente sob "Chegada P.A.1". */}
        <Box style={{ display: 'flex', flexDirection: 'column', gap: isSplit ? 8 : 0 }}>
          <Box style={{ display: 'flex', alignItems: 'flex-start' }}>
            <Step id="arrived-pa" label={isSplit ? T('Chegada ao P.A. 1', 'Arrived at point 1') : T('Chegada ao PA', 'Arrived at delivery point')}
              icon={<PinDropIcon />}
              done={hasArrivedPa} ts={report.arrivedPaAt}
              disabled={!canExecute || (isManager ? false : atPa1 === false)}
              onClick={() => { if (atPa1 === false) setConfirmOverridePa('pa1'); else postEvent('arrived-pa'); }}
              onShowDetails={() => setMarkDetails({ kind: 'arrived-pa', label: T('Chegada ao PA', 'Arrived at delivery point'), ts: report.arrivedPaAt })} />
            <Connector done={hasArrivedPa} />
            <Step id="unloading" label={isSplit ? T('Entrega P.A. 1', 'Delivery point 1') : T('Entrega de produto iniciado', 'Delivery started')}
              icon={<LocalShippingIcon />}
              done={hasUnloaded} ts={null}
              disabled={!hasArrivedPa} onClick={() => setDialog('unloading')}
              onShowDetails={() => setMarkDetails({ kind: 'unloading', label: T('Entrega de produto iniciado', 'Delivery started') })} />
          </Box>
          {isSplit && (
            <Box style={{ display: 'flex', alignItems: 'flex-start' }}>
              <Step id="arrived-pa2" label={T('Chegada ao P.A. 2', 'Arrived at point 2')}
                icon={<PinDropIcon />}
                done={hasArrivedPa2} ts={report.arrivedPa2At}
                disabled={!canExecute || !hasUnloaded || (isManager ? false : atPa2 === false)}
                onClick={() => { if (atPa2 === false) setConfirmOverridePa('pa2'); else postEvent('arrived-pa2'); }}
                onShowDetails={() => setMarkDetails({ kind: 'arrived-pa2', label: T('Chegada ao P.A. 2', 'Arrived at point 2'), ts: report.arrivedPa2At })} />
              <Connector done={hasUnloaded2} />
              <Step id="unloading2" label={T('Entrega P.A. 2', 'Delivery point 2')}
                icon={<LocalShippingIcon />}
                done={hasUnloaded2} ts={null}
                disabled={!hasArrivedPa2} onClick={() => setDialog('unloading2')}
                onShowDetails={() => setMarkDetails({ kind: 'unloading2', label: T('Entrega P.A. 2', 'Delivery point 2') })} />
            </Box>
          )}
        </Box>
        <Connector done={hasUnloaded}
          durationLabel={fmtDuration(report.arrivedPaAt, report.completedAt)}
          distanceLabel={legKm('pa_entrega')} />
        <Step id="completed" label={T('Entrega de produto concluído', 'Delivery completed')}
          icon={<AssignmentTurnedInIcon />}
          done={hasCompleted} ts={report.completedAt}
          disabled={!canComplete} onClick={() => setDialog('completed')}
          onShowDetails={() => setMarkDetails({ kind: 'completed', label: T('Entrega de produto concluído', 'Delivery completed'), ts: report.completedAt })} />
        <Connector done={hasDepartedPa}
          durationLabel={fmtDuration(report.completedAt, report.departedPaAt)} />
        {/* Saída do P.A. — marco físico auto-detetado por GPS (viatura afastou-se
            do posto). Alimenta o alerta 🟢 na listagem. Tem clique manual de
            recurso + "apagar marco", como os outros marcos automáticos. */}
        <Step id="departed-pa" label={T('Saída do P.A.', 'Left delivery point')}
          icon={<ExitToAppIcon />}
          done={hasDepartedPa} ts={report.departedPaAt}
          disabled={!hasUnloaded} onClick={() => postEvent('departed-pa')}
          onShowDetails={() => setMarkDetails({ kind: 'departed-pa', label: T('Saída do P.A.', 'Left delivery point'), ts: report.departedPaAt })} />
        <Connector done={hasReturned}
          durationLabel={fmtDuration(report.departedPaAt || report.completedAt, report.returnedBaseAt)}
          distanceLabel={legKm('entrega_base')} />
        {/* "Fim de viagem" só finaliza depois de: entrega concluída, escolher o
            próximo destino (Base/Terminal) E a "Saída do P.A." estar marcada
            (impõe a ordem dos marcos — pedido Move Express). */}
        <Step id="returned-base"
          label={T('Fim de viagem', 'Trip end')}
          icon={goTerminal ? <LocalGasStationIcon /> : <HomeWorkIcon />}
          done={hasReturned} ts={report.returnedBaseAt}
          subLabel={hasReturned || order.nextDestination ? tripEndName : null}
          disabled={!hasCompleted || !order.nextDestination || !hasDepartedPa} onClick={() => postEvent('returned-base')}
          onShowDetails={() => setMarkDetails({ kind: 'returned', label: T('Fim de viagem', 'Trip end'), ts: report.returnedBaseAt })} />
      </Box>

      {/* Fase B — Entrega dividida por 2 P.A. Configuração da divisão (2.º posto
          + litros por posto). Disponível enquanto a OT não está concluída. */}
      {!hasCompleted && (
        <Box style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {isSplit ? (
            <>
              <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 999,
                fontSize: '0.68rem', fontWeight: 800, border: '1.5px solid #fbbf24', background: '#fffbeb', color: '#92400e' }}>
                🔀 {T('Entrega dividida', 'Split delivery')}
                {order.deliveryName2 ? ` · ${T('P.A.2', 'Point 2')}: ${order.deliveryName2}` : ''}
                {order.plannedQty2 != null ? ` · ${Number(order.plannedQty2).toLocaleString('pt-PT')} L` : ''}
              </Box>
              <Box onClick={() => setSplitForm({
                deliveryName2: order.deliveryName2 || '', deliveryAddress2: order.deliveryAddress2 || '',
                deliveryLat2: order.deliveryLat2 != null ? Number(order.deliveryLat2) : null,
                deliveryLng2: order.deliveryLng2 != null ? Number(order.deliveryLng2) : null,
                plannedQty1: order.plannedQty1 != null ? Number(order.plannedQty1) : '',
                plannedQty2: order.plannedQty2 != null ? Number(order.plannedQty2) : '',
              })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none',
                  padding: '4px 10px', borderRadius: 999, fontSize: '0.66rem', fontWeight: 700,
                  border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b' }}>
                {T('Editar', 'Edit')}
              </Box>
            </>
          ) : pa1Ready ? (
            <Box onClick={() => setSplitForm({ deliveryName2: '', deliveryAddress2: '', deliveryLat2: null, deliveryLng2: null, plannedQty1: '', plannedQty2: '' })}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none',
                padding: '4px 12px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700,
                border: '1.5px solid #fed7aa', background: '#fff7ed', color: '#9a3412' }}>
              🔀 {T('Dividir entrega por 2 P.A.', 'Split delivery across 2 points')}
            </Box>
          ) : (
            <Box title={T('Preenche primeiro o 1.º P.A. (nome + GPS) no “Carregou / Revelar PA real”.', 'Fill in the 1st delivery point (name + GPS) first.')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, userSelect: 'none', cursor: 'not-allowed',
                padding: '4px 12px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700,
                border: '1.5px dashed #e2e8f0', background: '#f8fafc', color: '#94a3b8' }}>
              🔀 {T('Dividir entrega por 2 P.A.', 'Split delivery across 2 points')}
              <span style={{ fontSize: '0.6rem', fontWeight: 600 }}>· {T('preenche o 1.º P.A. primeiro', 'fill 1st point first')}</span>
            </Box>
          )}
        </Box>
      )}

      {/* Ponto 4 — alterar o P.A. de destino (motorista redirecionado). Disponível
          desde a saída da base até ao regresso. */}
      {hasDeparted && !hasReturned && (
        <Box style={{ marginTop: 8 }}>
          <Box onClick={() => setPaForm({
            deliveryName: order.deliveryName || '',
            deliveryAddress: order.deliveryAddress || '',
            deliveryLat: order.deliveryLat != null ? Number(order.deliveryLat) : null,
            deliveryLng: order.deliveryLng != null ? Number(order.deliveryLng) : null,
          })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none',
              padding: '4px 12px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700,
              border: '1.5px solid #c7d2fe', background: '#eef2ff', color: '#4338ca' }}>
            <PinDropIcon style={{ fontSize: 14 }} /> {T('Alterar P.A. de destino', 'Change delivery point')}
          </Box>
        </Box>
      )}

      {/* Ponto 2 — próximo destino após a entrega: Base (regresso) ou Terminal
          (segue direto para novo carregamento). Disponível a partir da conclusão
          da entrega e enquanto o regresso não foi marcado. ESCOLHA OBRIGATÓRIA
          antes de "Fim de viagem" — sem escolha nenhuma opção fica activa (não há
          default) para o operador não julgar que já está registado. */}
      {hasUnloaded && !hasReturned && (
        <Box style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Typography style={{ fontSize: '0.66rem', fontWeight: 700, color: order.nextDestination ? '#64748b' : '#b91c1c' }}>
            {T('Próximo destino:', 'Next destination:')}
            {!order.nextDestination && <span style={{ color: '#b91c1c' }}> *</span>}
          </Typography>
          {[
            { key: 'base', label: T('Regressar à Base', 'Return to base'), icon: '🏠' },
            { key: 'terminal', label: T('Seguir para o Terminal', 'Go to terminal'), icon: '⛽' },
          ].map(opt => {
            // Activo só quando a opção foi REALMENTE escolhida (sem default 'base').
            const active = order.nextDestination === opt.key;
            return (
              <Box key={opt.key} onClick={() => changeNextDest(opt.key)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none',
                  padding: '4px 12px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700,
                  border: `1.5px solid ${active ? '#f97316' : (order.nextDestination ? '#e2e8f0' : '#fca5a5')}`,
                  background: active ? '#fff7ed' : '#fff', color: active ? '#9a3412' : '#64748b',
                }}>
                <span>{opt.icon}</span>{opt.label}
              </Box>
            );
          })}
          {!order.nextDestination && (
            <Typography style={{ fontSize: '0.62rem', fontWeight: 700, color: '#b91c1c' }}>
              {T('Escolha obrigatória antes de finalizar a viagem', 'Required before ending the trip')}
            </Typography>
          )}
          {/* Ordem dos marcos: a "Saída do P.A." tem de estar marcada antes do
              "Fim de viagem". Normalmente é automática (GPS); em OTs sem
              movimento pode ser preciso marcá-la manualmente. */}
          {hasCompleted && order.nextDestination && !hasDepartedPa && (
            <Typography style={{ fontSize: '0.62rem', fontWeight: 700, color: '#b45309' }}>
              {T('Marque a “Saída do P.A.” antes de finalizar a viagem', 'Mark “Left delivery point” before ending the trip')}
            </Typography>
          )}
        </Box>
      )}

      {/* Ponto 4 — dialog "Alterar P.A. de destino" */}
      <Dialog open={!!paForm} onClose={() => setPaForm(null)} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 12 } }}>
        <DialogTitle style={{ fontSize: '0.9rem', fontWeight: 800, color: '#9a3412' }}>
          {T('Alterar P.A. de destino', 'Change delivery point')}
        </DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: 10 }}>
            {T('O motorista foi redirecionado para outro posto. A alteração fica registada no histórico da OT.',
              'The driver was redirected to another point. The change is logged in the order history.')}
          </Typography>
          {paForm && (
            <>
              {/* Favoritos — escolher o novo P.A. dos locais guardados (com pesquisa). */}
              {favorites.length > 0 && (() => {
                const q = paFavQuery.trim().toLowerCase();
                const base = favorites.filter(f => !q
                  || String(f.name || '').toLowerCase().includes(q)
                  || String(f.address || '').toLowerCase().includes(q));
                const filtered = paFavDir === 'desc' ? [...base].reverse() : base;
                const applyFav = (f) => {
                  setPaForm(p => ({
                    ...p,
                    deliveryName: f.name || '',
                    deliveryAddress: f.address || '',
                    deliveryLat: f.latitude != null ? Number(f.latitude) : null,
                    deliveryLng: f.longitude != null ? Number(f.longitude) : null,
                  }));
                  closePaFav();
                };
                return (
                  <Box style={{ marginBottom: 10 }}>
                    <Button
                      size="small"
                      startIcon={<StarIcon style={{ fontSize: 14 }} />}
                      onClick={e => setPaFavAnchor(e.currentTarget)}
                      style={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 700, color: '#0369a1', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '5px 12px' }}
                    >
                      {T(`Escolher dos favoritos (${favorites.length})`, `Pick from favourites (${favorites.length})`)}
                    </Button>
                    <Menu
                      anchorEl={paFavAnchor} open={!!paFavAnchor} onClose={closePaFav}
                      getContentAnchorEl={null}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      PaperProps={{ style: { maxHeight: 380, minWidth: 320 } }}
                    >
                      <Box style={{ padding: '6px 10px 8px', position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 6, alignItems: 'center' }}>
                        <TextField
                          autoFocus size="small" variant="outlined" style={{ flex: 1 }}
                          placeholder={T('Pesquisar favorito…', 'Search favourite…')}
                          value={paFavQuery}
                          onChange={e => setPaFavQuery(e.target.value)}
                          onKeyDown={e => e.stopPropagation()}
                          onClick={e => e.stopPropagation()}
                        />
                        <FavSortBtn dir={paFavDir} onToggle={() => setPaFavDir(d => d === 'asc' ? 'desc' : 'asc')} />
                      </Box>
                      {filtered.length === 0 ? (
                        <MenuItem disabled style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{T('Sem resultados', 'No matches')}</MenuItem>
                      ) : filtered.map(f => (
                        <MenuItem key={f.id} onClick={() => applyFav(f)} style={{ fontSize: '0.82rem' }}>
                          <StarIcon style={{ fontSize: 14, color: '#f59e0b', marginRight: 8 }} />
                          <Box>
                            <div style={{ fontWeight: 700 }}>{f.name}</div>
                            <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{f.address}</div>
                          </Box>
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                );
              })()}
              <TextField
                label={T('Nome do P.A.', 'Delivery point name')} value={paForm.deliveryName || ''}
                onChange={e => setPaForm(p => ({ ...p, deliveryName: e.target.value }))}
                fullWidth size="small" style={{ marginBottom: 12 }} />
              <AddressFieldShared
                label={T('Morada do P.A.', 'Delivery point address')} value={paForm.deliveryAddress || ''}
                onChange={txt => setPaForm(p => ({ ...p, deliveryAddress: txt }))}
                onSelect={geo => setPaForm(p => ({ ...p, deliveryAddress: geo.name, deliveryLat: geo.coordinates?.[1] ?? null, deliveryLng: geo.coordinates?.[0] ?? null }))}
                country="AO"
                helperText={T('Escreve 3+ letras para sugestões com GPS', 'Type 3+ letters for GPS suggestions')} />
              {paForm.deliveryLat != null && paForm.deliveryLng != null && (
                <Typography style={{ fontSize: '0.66rem', color: '#15803d', fontWeight: 700, marginTop: 6 }}>
                  ✓ GPS: {Number(paForm.deliveryLat).toFixed(5)}, {Number(paForm.deliveryLng).toFixed(5)}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaForm(null)} style={{ textTransform: 'none', color: '#64748b' }}>{T('Cancelar', 'Cancel')}</Button>
          <Button variant="contained" disabled={!paForm?.deliveryName?.trim()} onClick={() => changePA(paForm)}
            style={{ textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #fb923c, #f97316)', color: '#fff' }}>
            {T('Guardar', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Override do GESTOR — confirmar "Chegada ao P.A." fora da zona do P.A.
          definido como destino (pedido Move Express). Só chega aqui quem é
          gestor (o botão fica bloqueado para operador/viewer fora da zona). */}
      <Dialog open={!!confirmOverridePa} onClose={() => setConfirmOverridePa(null)} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 12 } }}>
        <DialogTitle style={{ fontSize: '0.95rem', fontWeight: 800, color: '#9a3412' }}>
          📍 {T('Confirmar chegada manual', 'Confirm manual arrival')}
        </DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: '0.84rem', color: '#334155', lineHeight: 1.55 }}>
            {T('A viatura ainda não se encontra no P.A. definido como destino. Confirmar manualmente a "Chegada ao P.A."?',
              'The vehicle is not yet at the delivery point set as destination. Manually confirm "Arrived at delivery point"?')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOverridePa(null)} style={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}>
            {T('Cancelar', 'Cancel')}
          </Button>
          <Button variant="contained"
            onClick={() => { const which = confirmOverridePa; setConfirmOverridePa(null); postEvent(which === 'pa2' ? 'arrived-pa2' : 'arrived-pa'); }}
            style={{ textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #fb923c, #f97316)', color: '#fff' }}>
            {T('Confirmar chegada', 'Confirm arrival')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fase B — dialog "Entrega dividida por 2 P.A." */}
      <Dialog open={!!splitForm} onClose={() => setSplitForm(null)} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 12 } }}>
        <DialogTitle style={{ fontSize: '0.9rem', fontWeight: 800, color: '#9a3412' }}>
          🔀 {T('Entrega dividida por 2 P.A.', 'Split delivery across 2 points')}
        </DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: 10 }}>
            {T('A mesma cisterna descarrega em 2 postos. Indica o 2.º posto e os litros por posto (definidos na ordem do cliente).',
              'The same cistern discharges at 2 points. Enter the 2nd point and the litres per point (from the client order).')}
          </Typography>
          {splitForm && (() => {
            // Total de referência: carga real, senão litros esperados.
            const ref = loadedQty != null ? loadedQty : (order.expectedLitres != null ? Number(order.expectedLitres) : null);
            const q1 = splitForm.plannedQty1 !== '' && splitForm.plannedQty1 != null ? Number(splitForm.plannedQty1) : null;
            const q2 = splitForm.plannedQty2 !== '' && splitForm.plannedQty2 != null ? Number(splitForm.plannedQty2) : null;
            const sum = (q1 || 0) + (q2 || 0);
            const mismatch = ref != null && (q1 != null || q2 != null) && Math.abs(sum - ref) > 1;
            return (
              <>
                {/* #7 — 1.º P.A. (registado em order.deliveryName). Mostrado para
                    referência; edita-se em "Alterar P.A. de destino" / "Revelar PA". */}
                <Box style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  <Typography style={{ fontSize: '0.62rem', fontWeight: 800, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
                    {T('1.º Posto de abastecimento', '1st delivery point')}
                  </Typography>
                  <Typography style={{ fontSize: '0.86rem', fontWeight: 700, color: order.deliveryName ? '#0f172a' : '#cbd5e1' }}>
                    {order.deliveryName || T('(por preencher)', '(not set)')}
                  </Typography>
                  {order.deliveryAddress ? (
                    <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>{order.deliveryAddress}</Typography>
                  ) : null}
                </Box>
                <Typography style={{ fontSize: '0.62rem', fontWeight: 800, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  {T('2.º Posto de abastecimento', '2nd delivery point')}
                </Typography>
                {/* Favoritos — escolher o 2.º P.A. dos locais guardados (com pesquisa). */}
                {favorites.length > 0 && (() => {
                  const q = splitFavQuery.trim().toLowerCase();
                  const base = favorites.filter(f => !q
                    || String(f.name || '').toLowerCase().includes(q)
                    || String(f.address || '').toLowerCase().includes(q));
                  const filtered = splitFavDir === 'desc' ? [...base].reverse() : base;
                  const applyFav = (f) => {
                    setSplitForm(p => ({
                      ...p,
                      deliveryName2: f.name || '',
                      deliveryAddress2: f.address || '',
                      deliveryLat2: f.latitude != null ? Number(f.latitude) : null,
                      deliveryLng2: f.longitude != null ? Number(f.longitude) : null,
                    }));
                    closeSplitFav();
                  };
                  return (
                    <Box style={{ marginBottom: 10 }}>
                      <Button
                        size="small"
                        startIcon={<StarIcon style={{ fontSize: 14 }} />}
                        onClick={e => setSplitFavAnchor(e.currentTarget)}
                        style={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 700, color: '#0369a1', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '5px 12px' }}
                      >
                        {T(`Escolher dos favoritos (${favorites.length})`, `Pick from favourites (${favorites.length})`)}
                      </Button>
                      <Menu
                        anchorEl={splitFavAnchor} open={!!splitFavAnchor} onClose={closeSplitFav}
                        getContentAnchorEl={null}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        PaperProps={{ style: { maxHeight: 380, minWidth: 320 } }}
                      >
                        <Box style={{ padding: '6px 10px 8px', position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 6, alignItems: 'center' }}>
                          <TextField
                            autoFocus size="small" variant="outlined" style={{ flex: 1 }}
                            placeholder={T('Pesquisar favorito…', 'Search favourite…')}
                            value={splitFavQuery}
                            onChange={e => setSplitFavQuery(e.target.value)}
                            onKeyDown={e => e.stopPropagation()}
                            onClick={e => e.stopPropagation()}
                          />
                          <FavSortBtn dir={splitFavDir} onToggle={() => setSplitFavDir(d => d === 'asc' ? 'desc' : 'asc')} />
                        </Box>
                        {filtered.length === 0 ? (
                          <MenuItem disabled style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{T('Sem resultados', 'No matches')}</MenuItem>
                        ) : filtered.map(f => (
                          <MenuItem key={f.id} onClick={() => applyFav(f)} style={{ fontSize: '0.82rem' }}>
                            <StarIcon style={{ fontSize: 14, color: '#f59e0b', marginRight: 8 }} />
                            <Box>
                              <div style={{ fontWeight: 700 }}>{f.name}</div>
                              <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{f.address}</div>
                            </Box>
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  );
                })()}
                <TextField
                  label={T('Nome do P.A. 2', 'Point 2 name')} value={splitForm.deliveryName2 || ''}
                  onChange={e => setSplitForm(p => ({ ...p, deliveryName2: e.target.value }))}
                  fullWidth size="small" style={{ marginBottom: 12 }} />
                <AddressFieldShared
                  label={T('Morada do P.A. 2', 'Point 2 address')} value={splitForm.deliveryAddress2 || ''}
                  onChange={txt => setSplitForm(p => ({ ...p, deliveryAddress2: txt }))}
                  onSelect={geo => setSplitForm(p => ({ ...p, deliveryAddress2: geo.name, deliveryLat2: geo.coordinates?.[1] ?? null, deliveryLng2: geo.coordinates?.[0] ?? null }))}
                  country="AO"
                  helperText={T('Escreve 3+ letras para sugestões com GPS', 'Type 3+ letters for GPS suggestions')} />
                {splitForm.deliveryLat2 != null && splitForm.deliveryLng2 != null && (
                  <Typography style={{ fontSize: '0.66rem', color: '#15803d', fontWeight: 700, marginTop: 6 }}>
                    ✓ GPS: {Number(splitForm.deliveryLat2).toFixed(5)}, {Number(splitForm.deliveryLng2).toFixed(5)}
                  </Typography>
                )}
                <Typography style={{ fontSize: '0.62rem', fontWeight: 800, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.5, margin: '14px 0 6px' }}>
                  {T('Litros por posto', 'Litres per point')}
                </Typography>
                <Box style={{ display: 'flex', gap: 10 }}>
                  <TextField label={T('P.A. 1 (L)', 'Point 1 (L)')} type="number" value={splitForm.plannedQty1}
                    onChange={e => setSplitForm(p => ({ ...p, plannedQty1: e.target.value }))} fullWidth size="small" />
                  <TextField label={T('P.A. 2 (L)', 'Point 2 (L)')} type="number" value={splitForm.plannedQty2}
                    onChange={e => setSplitForm(p => ({ ...p, plannedQty2: e.target.value }))} fullWidth size="small" />
                </Box>
                <Typography style={{ fontSize: '0.66rem', marginTop: 8, fontWeight: 700, color: mismatch ? '#dc2626' : '#64748b' }}>
                  {ref != null
                    ? `Σ ${sum.toLocaleString('pt-PT')} L ${mismatch ? '≠' : '='} ${ref.toLocaleString('pt-PT')} L (${loadedQty != null ? T('carga', 'loaded') : T('esperado', 'expected')})`
                    : `Σ ${sum.toLocaleString('pt-PT')} L`}
                  {mismatch ? ` — ${T('a soma não bate com a carga', 'sum does not match the load')}` : ''}
                </Typography>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions style={{ justifyContent: 'space-between' }}>
          <Box>
            {isSplit && (
              <Button onClick={clearSplit} style={{ textTransform: 'none', color: '#dc2626', fontWeight: 700 }}>
                {T('Remover divisão', 'Remove split')}
              </Button>
            )}
          </Box>
          <Box>
            <Button onClick={() => setSplitForm(null)} style={{ textTransform: 'none', color: '#64748b' }}>{T('Cancelar', 'Cancel')}</Button>
            <Button variant="contained" disabled={!splitForm?.deliveryName2?.trim()} onClick={() => saveSplit(splitForm)}
              style={{ textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #fb923c, #f97316)', color: '#fff' }}>
              {T('Guardar', 'Save')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Total real da rota — km GPS efectivos + tempo real (Saída da base → Regresso) */}
      {legMetrics && (legMetrics.totalKm != null || legMetrics.traveledKm != null || legMetrics.totalMinutes != null) && (
        <Box style={{ marginTop: 10, display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* Total da rota (soma dos troços entre marcos) + tempo total */}
          {(legMetrics.totalKm != null || legMetrics.totalMinutes != null) && (
            <Box style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
              border: '1.5px solid #bbf7d0', borderRadius: 999, padding: '5px 16px',
              fontSize: '0.72rem', fontWeight: 800, color: '#15803d',
              fontFamily: 'ui-monospace, Menlo, monospace',
            }}>
              <span>🏁 {T('Total da rota', 'Route total')}:</span>
              {legMetrics.totalKm != null && <span style={{ color: '#1e40af' }}>{fmtKm(legMetrics.totalKm)}</span>}
              {legMetrics.totalMinutes != null && (
                <span>· {(() => { const h = Math.floor(legMetrics.totalMinutes / 60); const m = legMetrics.totalMinutes % 60; return h > 0 ? `${h}h ${m}min` : `${m}min`; })()}</span>
              )}
            </Box>
          )}
          {/* Total percorrido (GPS real, mesmo a meio da viagem) */}
          {legMetrics.traveledKm != null && (
            <Box style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
              border: '1.5px solid #bfdbfe', borderRadius: 999, padding: '5px 16px',
              fontSize: '0.72rem', fontWeight: 800, color: '#1e40af',
              fontFamily: 'ui-monospace, Menlo, monospace',
            }}>
              <span>📍 {T('Percorrido', 'Traveled')}:</span>
              <span>{fmtKm(legMetrics.traveledKm)}</span>
            </Box>
          )}
        </Box>
      )}

      {/* Resumo dos dados gravados */}
      {(hasLoaded || hasUnloaded) && (
        <Box style={{
          marginTop: 14, padding: '10px 12px', background: '#fff',
          border: '1px solid #fed7aa', borderRadius: 8,
          display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.72rem',
        }}>
          {hasLoaded && (
            <Box>
              <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {T('Carregamento', 'Loading')}
              </Typography>
              <Typography style={{ fontSize: '0.74rem', color: '#0f172a', fontWeight: 600 }}>
                Nº {order.loadingOrderRef || '—'}
              </Typography>
              <Typography style={{ fontSize: '0.74rem', color: '#0f172a' }}>
                {loadedQty != null ? `${loadedQty.toLocaleString('pt-PT')} L` : '—'}
              </Typography>
              {order.loadingSealNumber && (
                <Typography style={{ fontSize: '0.66rem', color: '#1e40af', fontFamily: 'ui-monospace, Menlo, monospace', marginTop: 2 }}>
                  {T('Selo', 'Seal')}: {order.loadingSealNumber}
                </Typography>
              )}
            </Box>
          )}
          {hasUnloaded && (
            <Box>
              <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {isSplit ? T('Entrega P.A. 1 (Guia)', 'Delivery point 1 (Guide)') : T('Entrega (Guia)', 'Delivery (Guide)')}
              </Typography>
              <Typography style={{ fontSize: '0.74rem', color: '#0f172a', fontWeight: 600 }}>
                Nº {order.deliveryGuideRef || '—'}
              </Typography>
              <Typography style={{ fontSize: '0.74rem', color: '#0f172a' }}>
                {unloadQty != null ? `${unloadQty.toLocaleString('pt-PT')} L` : '—'}
              </Typography>
              {order.deliveryName && (
                <Typography style={{ fontSize: '0.62rem', color: '#64748b', marginTop: 2 }}>
                  {order.deliveryName}
                </Typography>
              )}
            </Box>
          )}
          {isSplit && hasUnloaded2 && (
            <Box>
              <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {T('Entrega P.A. 2 (Guia)', 'Delivery point 2 (Guide)')}
              </Typography>
              <Typography style={{ fontSize: '0.74rem', color: '#0f172a', fontWeight: 600 }}>
                Nº {order.deliveryGuideRef2 || '—'}
              </Typography>
              <Typography style={{ fontSize: '0.74rem', color: '#0f172a' }}>
                {unloadQty2 != null ? `${unloadQty2.toLocaleString('pt-PT')} L` : '—'}
              </Typography>
              {order.deliveryName2 && (
                <Typography style={{ fontSize: '0.62rem', color: '#64748b', marginTop: 2 }}>
                  {order.deliveryName2}
                </Typography>
              )}
            </Box>
          )}
          {divergence != null && (
            <Box>
              <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {T('Divergência', 'Divergence')}
              </Typography>
              <Typography style={{
                fontSize: '0.86rem', fontWeight: 800,
                color: Math.abs(divergence) > 100 ? '#dc2626' : '#16a34a',
              }}>
                {divergence > 0 ? '−' : '+'}{Math.abs(divergence).toLocaleString('pt-PT')} L
              </Typography>
              {order.divergenceReason && (
                <Typography style={{ fontSize: '0.62rem', color: '#475569', fontStyle: 'italic', marginTop: 2 }}>
                  "{order.divergenceReason}"
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {error && (
        <Typography style={{ marginTop: 8, fontSize: '0.7rem', color: '#dc2626' }}>
          ⚠ {error}
        </Typography>
      )}

      {/* Dialog: Carregou no terminal. Campos do documento real Sonangol —
          os primeiros 2 são essenciais; os restantes 4 são opcionais e
          servem facturação ao cliente (confirmados pelo doc da Move Express
          2026-06-25). A Robot IA na Fase 1B preencherá tudo a partir da foto.

          Secção extra "PA real" — aparece SÓ se a OT foi criada em modo
          "PA por confirmar". É neste momento que o PA exacto é revelado
          (vem na Ordem de Carregamento que o motorista fotografa). */}
      <FuelEventDialog
        lang={lang}
        open={dialog === 'loaded'}
        title={T(`Confirmar carregamento (Ordem ${fuelCustomerLabel})`, `Confirm loading (${fuelCustomerLabel} Order)`)}
        onClose={() => setDialog(null)}
        busy={busy === 'loaded'}
        fields={[
          { name: 'loadingOrderRef', label: T('Nº Ordem de Carregamento *', 'Loading Order Nº *'), placeholder: 'ex.: 6100085032' },
          { name: 'loadingOrderQty', label: T('Quantidade carregada (L) *', 'Loaded quantity (L) *'), type: 'number', placeholder: '34850' },
          // Nº do selo — SÓ TOTAL (outros fornecedores não usam selo na guia).
          // Fallback ao prefixo do customRef ("Tot-") para OTs antigas sem
          // fuelCustomer preenchido.
          ...((order.fuelCustomer === 'total' || (order.customRef || '').startsWith('Tot-')) ? [
            { name: 'loadingSealNumber', label: T('Nº do selo (TOTAL) *', 'Seal Nº (TOTAL) *'), placeholder: 'ex.: 12345678' },
          ] : []),
          { name: 'loadingPedidoSnld', label: T('Nº Pedido SNLD (opcional)', 'SNLD Request Nº (optional)'), placeholder: 'ex.: 159180644' },
          { name: 'loadingDocRef', label: T('Doc. Ref (opcional)', 'Doc Ref (optional)'), placeholder: 'ex.: 80179268' },
          { name: 'loadingInstal', label: T('Instal. (opcional)', 'Install. (optional)'), placeholder: 'ex.: 349' },
          { name: 'loadingMaterialCode', label: T('Cód. material (opcional)', 'Material code (optional)'), placeholder: 'ex.: 900130' },
        ]}
        photoField="loadingPhotoUrl"
        extraSection={({ values, setValues }) => (
          <>
            {(!order.deliveryName && !order.deliveryAddress) && (
              <RevealPABlock
                values={values}
                setValues={setValues}
                favorites={favorites}
                lang={lang}
              />
            )}
            {/* Piloto automático — só faz sentido quando já há rota para
                recalcular. Default LIGADO; o operador pode desligar. */}
            {order.assignedRouteId && (() => {
              const on = values._autopilot !== false;
              return (
                <Box onClick={() => setValues(prev => ({ ...prev, _autopilot: !on }))}
                  style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none',
                    background: on ? '#f0fdf4' : '#f8fafc', border: `1.5px solid ${on ? '#bbf7d0' : '#e2e8f0'}`, borderRadius: 12, padding: '12px 14px' }}>
                  <Box style={{ width: 40, height: 22, borderRadius: 999, background: on ? '#16a34a' : '#cbd5e1', position: 'relative', flexShrink: 0, transition: 'background 0.15s' }}>
                    <Box style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Typography style={{ fontSize: '0.8rem', fontWeight: 800, color: on ? '#15803d' : '#475569' }}>
                      {T('🚀 Piloto automático da rota', '🚀 Route autopilot')}
                    </Typography>
                    <Typography style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 1, lineHeight: 1.35 }}>
                      {on
                        ? T('Ao confirmar, calcula e grava a rota sozinho e volta à cronologia (sem ir ao planeamento).', 'On confirm, it calculates and saves the route by itself and returns to the timeline (no planner).')
                        : T('Desligado: ao confirmar, vai ao planeamento para calcular/gravar manualmente.', 'Off: on confirm, opens the planner to calculate/save manually.')}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
          </>
        )}
        onSubmit={(values) => postEvent('loaded', values)}
      />

      {/* Dialog: Descarregou no PA */}
      <FuelEventDialog
        lang={lang}
        open={dialog === 'unloading'}
        title={T('Confirmar entrega (Guia de Conformidade)', 'Confirm delivery (Compliance Guide)')}
        onClose={() => setDialog(null)}
        busy={busy === 'unloading'}
        fields={[
          { name: 'deliveryGuideRef', label: T('Nº da Guia de Conformidade', 'Compliance Guide Nº'), placeholder: 'ex.: GC-2026-04590' },
          { name: 'deliveryGuideQty', label: T('Quantidade descarregada (litros)', 'Unloaded quantity (litres)'), type: 'number', placeholder: '34700' },
          { name: 'cisternMeasureLitres', label: T('Litros medidos na cisterna do veículo', 'Litres measured in vehicle cistern'), type: 'number', placeholder: '34650' },
        ]}
        extraSection={({ values, setValues }) => (
          <Box style={{ background: '#fff', border: '1px solid #fed7aa', borderRadius: 12, padding: '14px 16px' }}>
            <Typography style={{ fontSize: '0.62rem', fontWeight: 800, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
              {T('Confirmação do selo (anti-roubo)', 'Seal check (anti-theft)')} <span style={{ color: '#dc2626' }}>* {T('obrigatório', 'required')}</span>
            </Typography>
            <Typography style={{ fontSize: '0.78rem', color: '#334155', marginBottom: 8 }}>
              {T('O veículo cisterna chegou selado (selo intacto)?', 'Did the cistern arrive sealed (seal intact)?')}
            </Typography>
            <Box style={{ display: 'flex', gap: 8 }}>
              {[
                { v: true, label: T('✓ Sim, selado', '✓ Yes, sealed'), color: '#15803d', bg: '#dcfce7', br: '#bbf7d0' },
                { v: false, label: T('⚠ Não, selo violado', '⚠ No, seal violated'), color: '#b91c1c', bg: '#fee2e2', br: '#fecaca' },
              ].map(opt => {
                const active = values.deliverySealIntact === opt.v;
                return (
                  <Box key={String(opt.v)} onClick={() => setValues(prev => ({ ...prev, deliverySealIntact: opt.v }))}
                    style={{
                      cursor: 'pointer', flex: 1, textAlign: 'center', padding: '9px 12px', borderRadius: 8,
                      fontSize: '0.8rem', fontWeight: 800, userSelect: 'none',
                      background: active ? opt.bg : '#fff', color: active ? opt.color : '#64748b',
                      border: `1.5px solid ${active ? opt.br : '#e2e8f0'}`,
                    }}>
                    {opt.label}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
        photoField="deliveryPhotoUrl"
        validate={({ values, photo }) => {
          if (values.deliverySealIntact == null) return T('Confirme o selo (Sim / Não) antes de confirmar a entrega.', 'Confirm the seal (Yes / No) before confirming the delivery.');
          if (!String(values.deliveryGuideRef || '').trim() && !photo) return T('Indique o Nº da Guia de Conformidade ou anexe a foto da guia.', 'Enter the Compliance Guide Nº or attach the guide photo.');
          return null;
        }}
        onSubmit={(values) => postEvent('unloading', values)}
      />

      {/* Fase B — Dialog: Descarregou no 2.º P.A. (espelha o de cima) */}
      <FuelEventDialog
        lang={lang}
        open={dialog === 'unloading2'}
        title={T('Confirmar entrega no P.A. 2 (Guia de Conformidade)', 'Confirm delivery at point 2 (Compliance Guide)')}
        onClose={() => setDialog(null)}
        busy={busy === 'unloading2'}
        fields={[
          { name: 'deliveryGuideRef', label: T('Nº da Guia de Conformidade (P.A. 2)', 'Compliance Guide Nº (point 2)'), placeholder: 'ex.: GC-2026-04591' },
          { name: 'deliveryGuideQty', label: T('Quantidade descarregada no P.A. 2 (litros)', 'Unloaded quantity at point 2 (litres)'), type: 'number', placeholder: '15000' },
        ]}
        extraSection={({ values, setValues }) => (
          <Box style={{ background: '#fff', border: '1px solid #fed7aa', borderRadius: 12, padding: '14px 16px' }}>
            <Typography style={{ fontSize: '0.62rem', fontWeight: 800, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
              {T('Confirmação do selo (anti-roubo)', 'Seal check (anti-theft)')} <span style={{ color: '#dc2626' }}>* {T('obrigatório', 'required')}</span>
            </Typography>
            <Typography style={{ fontSize: '0.78rem', color: '#334155', marginBottom: 8 }}>
              {T('O veículo cisterna chegou selado ao P.A. 2?', 'Did the cistern arrive sealed at point 2?')}
            </Typography>
            <Box style={{ display: 'flex', gap: 8 }}>
              {[
                { v: true, label: T('✓ Sim, selado', '✓ Yes, sealed'), color: '#15803d', bg: '#dcfce7', br: '#bbf7d0' },
                { v: false, label: T('⚠ Não, selo violado', '⚠ No, seal violated'), color: '#b91c1c', bg: '#fee2e2', br: '#fecaca' },
              ].map(opt => {
                const active = values.deliverySealIntact === opt.v;
                return (
                  <Box key={String(opt.v)} onClick={() => setValues(prev => ({ ...prev, deliverySealIntact: opt.v }))}
                    style={{
                      cursor: 'pointer', flex: 1, textAlign: 'center', padding: '9px 12px', borderRadius: 8,
                      fontSize: '0.8rem', fontWeight: 800, userSelect: 'none',
                      background: active ? opt.bg : '#fff', color: active ? opt.color : '#64748b',
                      border: `1.5px solid ${active ? opt.br : '#e2e8f0'}`,
                    }}>
                    {opt.label}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
        photoField="deliveryPhotoUrl"
        validate={({ values, photo }) => {
          if (values.deliverySealIntact == null) return T('Confirme o selo do P.A. 2 (Sim / Não) antes de confirmar.', 'Confirm the seal at point 2 (Yes / No) before submitting.');
          if (!String(values.deliveryGuideRef || '').trim() && !photo) return T('Indique o Nº da Guia (P.A. 2) ou anexe a foto da guia.', 'Enter the Guide Nº (point 2) or attach the guide photo.');
          return null;
        }}
        onSubmit={(values) => postEvent('unloading2', values)}
      />

      {/* Dialog: Concluir (com motivo divergência se houver) */}
      {/* Dialog "Detalhes do marco" — abre quando o operador clica numa
          bola feita para rever o que foi gravado nesse momento (timestamp,
          ordem, qtd, foto, divergência, etc.). */}
      <Dialog
        open={!!markDetails}
        onClose={() => setMarkDetails(null)}
        maxWidth="sm" fullWidth
        PaperProps={{ style: {
          borderRadius: 14, overflow: 'hidden',
          maxHeight: '92vh',
          display: 'flex', flexDirection: 'column',
        } }}
      >
        {markDetails && (() => {
          const MarkIcon = {
            departed: DirectionsRunIcon,
            arrived: LocationOnIcon,
            'loading-started': LocalGasStationIcon,
            loaded: LocalGasStationIcon,
            'arrived-pa': PinDropIcon,
            unloading: LocalShippingIcon,
            'arrived-pa2': PinDropIcon,
            unloading2: LocalShippingIcon,
            completed: AssignmentTurnedInIcon,
            returned: HomeWorkIcon,
          }[markDetails.kind];
          return (
            <>
              <Box style={{
                padding: '16px 22px',
                background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 60%)',
                borderBottom: '1px solid #bbf7d0',
                display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <Box style={{
                  width: 42, height: 42, borderRadius: 11,
                  background: 'linear-gradient(135deg, #16a34a, #15803d)',
                  color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 3px 10px rgba(22,163,74,0.30)',
                }}>
                  {MarkIcon && <MarkIcon style={{ fontSize: 22 }} />}
                </Box>
                <Box style={{ flex: 1 }}>
                  <Typography style={{ fontSize: '0.62rem', fontWeight: 800, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    {T('Detalhes do marco', 'Milestone details')}
                  </Typography>
                  <Typography style={{ fontSize: '1.02rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>
                    {markDetails.label}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setMarkDetails(null)}>
                  <CloseIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              <Box style={{
                padding: '18px 22px', background: '#fafafa',
                display: 'flex', flexDirection: 'column', gap: 12,
                overflowY: 'auto', flex: '1 1 auto', minHeight: 0,
              }}>
                {/* Timestamp */}
                {markDetails.ts && (
                  <Box style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
                    <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {T('Quando', 'When')}
                    </Typography>
                    <Typography style={{ fontSize: '0.94rem', fontWeight: 800, color: '#0f172a', fontFamily: 'ui-monospace, Menlo, monospace', marginTop: 2 }}>
                      {new Date(markDetails.ts).toLocaleString(lang === 'pt' ? 'pt-PT' : 'en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                    </Typography>
                  </Box>
                )}
                {/* Apagar o marco (marcos de tempo puro) — repõe a null; o
                    auto-poller volta a acendê-lo quando a condição real ocorrer. */}
                {markDetails.ts && (() => {
                  const CLEARABLE = { departed: 'departedBaseAt', arrived: 'arrivedAt', 'loading-started': 'loadingStartedAt', 'departed-terminal': 'departedTerminalAt', 'arrived-pa': 'arrivedPaAt', 'departed-pa': 'departedPaAt', 'arrived-pa2': 'arrivedPa2At', returned: 'returnedBaseAt' };
                  const field = CLEARABLE[markDetails.kind];
                  if (!field) return null;
                  return (
                    <button type="button" disabled={busy === 'clear-milestone'}
                      onClick={async () => { await postEvent('clear-milestone', { field }); setMarkDetails(null); }}
                      style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '7px 12px', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer' }}>
                      <CloseIcon style={{ fontSize: 15 }} />
                      {T('Apagar marco (volta a acender sozinho)', 'Clear milestone (auto re-detects)')}
                    </button>
                  );
                })()}

                {/* Dados específicos por tipo de marco */}
                {markDetails.kind === 'loaded' && (
                  <Box style={{ background: '#fff', border: '1px solid #fed7aa', borderRadius: 10, padding: '12px 14px' }}>
                    <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                      {T('Ordem Sonangol', 'Sonangol Order')}
                    </Typography>
                    <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <Box>
                        <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>{T('Nº Ordem', 'Order Nº')}</Typography>
                        <Typography style={{ fontSize: '0.86rem', fontWeight: 700, fontFamily: 'ui-monospace, Menlo, monospace', color: '#0f172a' }}>
                          {order.loadingOrderRef || '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>{T('Quantidade', 'Quantity')}</Typography>
                        <Typography style={{ fontSize: '0.86rem', fontWeight: 700, fontFamily: 'ui-monospace, Menlo, monospace', color: '#0f172a' }}>
                          {order.loadingOrderQty != null ? `${Number(order.loadingOrderQty).toLocaleString('pt-PT')} L` : '—'}
                        </Typography>
                      </Box>
                      {order.loadingPedidoSnld && (
                        <Box>
                          <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>Nº Pedido SNLD</Typography>
                          <Typography style={{ fontSize: '0.82rem', fontWeight: 600, fontFamily: 'ui-monospace, Menlo, monospace', color: '#0f172a' }}>
                            {order.loadingPedidoSnld}
                          </Typography>
                        </Box>
                      )}
                      {order.loadingDocRef && (
                        <Box>
                          <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>Doc Ref</Typography>
                          <Typography style={{ fontSize: '0.82rem', fontWeight: 600, fontFamily: 'ui-monospace, Menlo, monospace', color: '#0f172a' }}>
                            {order.loadingDocRef}
                          </Typography>
                        </Box>
                      )}
                      {order.loadingInstal && (
                        <Box>
                          <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>Instal</Typography>
                          <Typography style={{ fontSize: '0.82rem', fontWeight: 600, fontFamily: 'ui-monospace, Menlo, monospace', color: '#0f172a' }}>
                            {order.loadingInstal}
                          </Typography>
                        </Box>
                      )}
                      {order.loadingMaterialCode && (
                        <Box>
                          <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>{T('Cód. material', 'Material code')}</Typography>
                          <Typography style={{ fontSize: '0.82rem', fontWeight: 600, fontFamily: 'ui-monospace, Menlo, monospace', color: '#0f172a' }}>
                            {order.loadingMaterialCode}
                          </Typography>
                        </Box>
                      )}
                      {/* Nº do selo — só TOTAL */}
                      {order.loadingSealNumber && (
                        <Box>
                          <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            {T('Nº do selo', 'Seal Nº')} {order.fuelCustomer === 'total' && (
                              <span style={{ background: '#dbeafe', color: '#1e40af', padding: '1px 6px', borderRadius: 999, marginLeft: 4, fontSize: '0.52rem' }}>TOTAL</span>
                            )}
                          </Typography>
                          <Typography style={{ fontSize: '0.82rem', fontWeight: 700, fontFamily: 'ui-monospace, Menlo, monospace', color: '#0f172a' }}>
                            {order.loadingSealNumber}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {loadingPhotoUrl && (
                      <Box style={{ marginTop: 12 }}>
                        <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{T('Foto da guia', 'Guide photo')}</Typography>
                        <Box onClick={() => setZoom({ url: loadingPhotoUrl, label: T('Guia de carregamento', 'Loading guide') })}
                          title={T('Clicar para ampliar', 'Click to enlarge')}
                          style={{ position: 'relative', cursor: 'zoom-in' }}>
                          <img src={loadingPhotoUrl} alt="Guia de carregamento" style={{
                            width: '100%', maxHeight: 220, objectFit: 'contain',
                            borderRadius: 8, border: '1px solid #e2e8f0', display: 'block',
                          }} />
                          <Box style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(15,23,42,0.62)', color: '#fff', borderRadius: 7, padding: '3px 5px', display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.6rem', fontWeight: 700 }}>
                            <ZoomInIcon style={{ fontSize: 14 }} /> {T('ampliar', 'zoom')}
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* PA de destino — revelado no momento do carregamento.
                    Mostra info + mini-mapa interactivo. */}
                {markDetails.kind === 'loaded' && (order.deliveryName || order.deliveryAddress) && (
                  <Box style={{ background: '#fff', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 14px' }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <Box style={{
                        width: 22, height: 22, borderRadius: 6,
                        background: '#dcfce7', color: '#15803d',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12,
                      }}>📍</Box>
                      <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {T('PA destino revelado', 'Delivery point revealed')}
                      </Typography>
                    </Box>
                    {order.deliveryName && (
                      <Typography style={{ fontSize: '0.94rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.005em' }}>
                        {order.deliveryName}
                      </Typography>
                    )}
                    {order.deliveryAddress && (
                      <Typography style={{ fontSize: '0.74rem', color: '#475569', marginTop: 3, lineHeight: 1.4 }}>
                        {order.deliveryAddress}
                      </Typography>
                    )}
                    {order.deliveryLat && order.deliveryLng && (
                      <>
                        <Box style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          fontSize: '0.66rem', color: '#15803d', fontWeight: 700,
                          background: '#dcfce7', border: '1px solid #86efac',
                          padding: '3px 10px', borderRadius: 999,
                          marginTop: 10,
                          fontFamily: 'ui-monospace, Menlo, monospace',
                        }}>
                          GPS: {Number(order.deliveryLat).toFixed(5)}, {Number(order.deliveryLng).toFixed(5)}
                        </Box>
                        {/* Mini-mapa Google Embed — sem precisar de API key,
                            usa o iframe público maps.google.com com pin. */}
                        <Box style={{
                          marginTop: 12, borderRadius: 8, overflow: 'hidden',
                          border: '1px solid #e2e8f0',
                          height: 180, position: 'relative',
                        }}>
                          <iframe
                            title="Localização do PA"
                            src={`https://maps.google.com/maps?q=${Number(order.deliveryLat)},${Number(order.deliveryLng)}&z=15&output=embed`}
                            width="100%" height="100%"
                            style={{ border: 0, display: 'block' }}
                            loading="lazy"
                          />
                        </Box>
                        <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                          <Button
                            size="small"
                            href={`https://www.google.com/maps/search/?api=1&query=${Number(order.deliveryLat)},${Number(order.deliveryLng)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              textTransform: 'none', fontSize: '0.7rem',
                              color: '#1e40af', fontWeight: 700,
                              padding: '2px 8px',
                            }}
                          >
                            {T('Abrir no Google Maps →', 'Open in Google Maps →')}
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                )}

                {markDetails.kind === 'unloading' && (
                  <Box style={{ background: '#fff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 14px' }}>
                    <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                      {T('Guia de Conformidade', 'Compliance Guide')}
                    </Typography>
                    <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <Box>
                        <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>{T('Nº Guia', 'Guide Nº')}</Typography>
                        <Typography style={{ fontSize: '0.86rem', fontWeight: 700, fontFamily: 'ui-monospace, Menlo, monospace', color: '#0f172a' }}>
                          {order.deliveryGuideRef || '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>{T('Quantidade', 'Quantity')}</Typography>
                        <Typography style={{ fontSize: '0.86rem', fontWeight: 700, fontFamily: 'ui-monospace, Menlo, monospace', color: '#0f172a' }}>
                          {order.deliveryGuideQty != null ? `${Number(order.deliveryGuideQty).toLocaleString('pt-PT')} L` : '—'}
                        </Typography>
                      </Box>
                    </Box>
                    {deliveryPhotoUrl && (
                      <Box style={{ marginTop: 12 }}>
                        <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{T('Foto da guia', 'Guide photo')}</Typography>
                        <Box onClick={() => setZoom({ url: deliveryPhotoUrl, label: T('Guia de descarga', 'Delivery guide') })}
                          title={T('Clicar para ampliar', 'Click to enlarge')}
                          style={{ position: 'relative', cursor: 'zoom-in' }}>
                          <img src={deliveryPhotoUrl} alt="Guia de descarga" style={{
                            width: '100%', maxHeight: 220, objectFit: 'contain',
                            borderRadius: 8, border: '1px solid #e2e8f0', display: 'block',
                          }} />
                          <Box style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(15,23,42,0.62)', color: '#fff', borderRadius: 7, padding: '3px 5px', display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.6rem', fontWeight: 700 }}>
                            <ZoomInIcon style={{ fontSize: 14 }} /> {T('ampliar', 'zoom')}
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {markDetails.kind === 'completed' && (
                  <>
                    {divergence != null && (
                      <Box style={{
                        background: Math.abs(divergence) > 100 ? '#fee2e2' : '#dcfce7',
                        border: `1px solid ${Math.abs(divergence) > 100 ? '#fca5a5' : '#86efac'}`,
                        borderRadius: 10, padding: '12px 14px',
                      }}>
                        <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: Math.abs(divergence) > 100 ? '#991b1b' : '#166534', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                          Divergência
                        </Typography>
                        <Typography style={{ fontSize: '1.05rem', fontWeight: 800, color: Math.abs(divergence) > 100 ? '#dc2626' : '#16a34a', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                          {divergence > 0 ? '−' : '+'}{Math.abs(divergence).toLocaleString('pt-PT')} L
                        </Typography>
                        {order.divergenceReason && (
                          <Typography style={{ fontSize: '0.78rem', color: '#475569', fontStyle: 'italic', marginTop: 6 }}>
                            "{order.divergenceReason}"
                          </Typography>
                        )}
                      </Box>
                    )}
                  </>
                )}

                {/* Início de viagem: repõe a informação de ONDE o veículo partiu
                    (o rótulo fixo "Início de viagem" deixou de a mostrar) + o
                    troço base→terminal. */}
                {markDetails.kind === 'departed' && (() => {
                  const depName = order.routeOrigin?.text || order.routeOrigin?.name || order.routeOrigin?.address || legMetrics?.departureName || null;
                  const depKm = legKm('base_terminal');
                  const depDur = fmtDuration(report.departedBaseAt, report.arrivedAt);
                  return (
                    <Box style={{ background: '#fff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 14px' }}>
                      <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                        {T('Local de partida', 'Departure location')}
                      </Typography>
                      <Typography style={{ fontSize: '0.9rem', fontWeight: 800, color: '#0f172a' }}>
                        {T(departureLabelPt, departureLabelEn)}
                      </Typography>
                      {depName && (
                        <Typography style={{ fontSize: '0.78rem', color: '#475569', marginTop: 2, lineHeight: 1.4 }}>
                          {depName}
                        </Typography>
                      )}
                      {(depKm || depDur) && (
                        <Box style={{ display: 'flex', gap: 14, marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e2e8f0' }}>
                          <Box>
                            <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>{T('Troço até ao terminal', 'Leg to terminal')}</Typography>
                            <Typography style={{ fontSize: '0.84rem', fontWeight: 700, color: '#1e40af', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                              {[depKm, depDur].filter(Boolean).join(' · ') || '—'}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })()}

                {(markDetails.kind === 'arrived' || markDetails.kind === 'arrived-pa' || markDetails.kind === 'returned') && (
                  <Typography style={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', padding: '8px 0' }}>
                    {T('Este marco só guarda o momento em que foi registado.', 'This milestone only records the moment it was registered.')}
                  </Typography>
                )}
              </Box>

              <Box style={{
                padding: '12px 22px', background: '#fff',
                borderTop: '1px solid #e2e8f0',
                display: 'flex', justifyContent: 'flex-end',
                flexShrink: 0,
              }}>
                <Button
                  onClick={() => setMarkDetails(null)}
                  variant="contained"
                  style={{
                    textTransform: 'none', fontWeight: 700,
                    background: 'linear-gradient(135deg, #16a34a, #15803d)',
                    color: '#fff',
                    padding: '6px 22px', borderRadius: 8,
                    boxShadow: '0 2px 6px rgba(22,163,74,0.25)',
                  }}
                >
                  {T('Fechar', 'Close')}
                </Button>
              </Box>
            </>
          );
        })()}
      </Dialog>

      {/* Dialog profissional "PA revelado, recalcular rota?" — substitui
          alert nativo do browser. Aparece depois do "Carregou" com sucesso
          se o PA foi preenchido E a OT já tinha rota assignada. */}
      <Dialog
        open={!!replanPrompt}
        onClose={() => setReplanPrompt(null)}
        maxWidth="sm" fullWidth
        PaperProps={{ style: { borderRadius: 14, overflow: 'hidden' } }}
      >
        {replanPrompt && (
          <>
            <Box style={{
              padding: '16px 22px',
              background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 60%)',
              borderBottom: '1px solid #bbf7d0',
              display: 'flex', alignItems: 'center', gap: 14,
            }}>
              <Box style={{
                width: 38, height: 38, borderRadius: 10,
                background: '#16a34a', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CheckCircleIcon style={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography style={{ fontSize: '1.02rem', fontWeight: 800, color: '#15803d', letterSpacing: '-0.01em' }}>
                  {replanPrompt.split
                    ? T('2.º P.A. gravado', '2nd delivery point saved')
                    : T('Carregamento gravado · PA revelado', 'Loading saved · Delivery point revealed')}
                </Typography>
                <Typography style={{ fontSize: '0.62rem', fontWeight: 800, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>
                  {T('Move Express · Distribuição', 'Move Express · Distribution')}
                </Typography>
              </Box>
            </Box>
            <Box style={{ padding: '18px 22px', background: '#fafafa' }}>
              <Box style={{
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
                padding: '12px 14px', marginBottom: 12,
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <Box style={{
                  width: 26, height: 26, borderRadius: 6,
                  background: '#fee2e2', color: '#dc2626',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, flexShrink: 0,
                }}>📍</Box>
                <Box>
                  <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {T('PA destino', 'Delivery point')}
                  </Typography>
                  <Typography style={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.35 }}>
                    {replanPrompt.paName}
                  </Typography>
                  {replanPrompt.paAddress && (
                    <Typography style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2 }}>
                      {replanPrompt.paAddress}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box style={{
                padding: '10px 14px',
                background: '#fef3c7', border: '1px solid #fcd34d',
                borderRadius: 8,
                fontSize: '0.78rem', color: '#92400e',
                lineHeight: 1.45,
              }}>
                {replanPrompt.split
                  ? (lang === 'pt'
                    ? <><strong>A rota actual (mapa e Live) não passa pelo 2.º P.A.</strong> Recalcula para o trajeto incluir P.A. 1 → P.A. 2 e os km ficarem certos. Abre em modo de edição — revê e clica <strong>Guardar</strong>. (Combustível não tem app de motorista, por isso não se reenvia nada.)</>
                    : <><strong>The current route (map and Live) does not pass through the 2nd point.</strong> Recalculate so the path includes P.A. 1 → P.A. 2 and the distance is correct. Opens in edit mode — review and click <strong>Save</strong>. (Fuel has no driver app, nothing is resent.)</>)
                  : (lang === 'pt'
                    ? <><strong>A rota actual aponta para o terminal de carga.</strong> Para o motorista ter o trajeto correcto até ao PA, é preciso recalcular a rota.</>
                    : <><strong>The current route ends at the loading terminal.</strong> To give the driver the correct path to the delivery point, the route must be recalculated.</>)}
              </Box>
            </Box>
            <Box style={{
              padding: '12px 22px', background: '#fff',
              borderTop: '1px solid #e2e8f0',
              display: 'flex', justifyContent: 'flex-end', gap: 8,
            }}>
              <Button
                onClick={() => setReplanPrompt(null)}
                style={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}
              >
                {T('Mais tarde', 'Later')}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  const rid = replanPrompt.routeId;
                  setReplanPrompt(null);
                  if (typeof onReplan === 'function') {
                    // Combustível: reconstruir sempre as paragens da OT (revelar
                    // P.A. no Carregou OU 2.º P.A. gravado).
                    onReplan(rid, { fuelRebuild: true });
                  }
                }}
                style={{
                  textTransform: 'none', fontWeight: 700,
                  background: 'linear-gradient(135deg, #16a34a, #15803d)',
                  color: '#fff',
                  padding: '7px 18px', borderRadius: 8,
                  boxShadow: '0 2px 6px rgba(22,163,74,0.25)',
                }}
              >
                {T('Recalcular rota agora →', 'Recalculate route now →')}
              </Button>
            </Box>
          </>
        )}
      </Dialog>

      <FuelEventDialog
        lang={lang}
        open={dialog === 'completed'}
        title={T('Concluir distribuição', 'Complete distribution')}
        onClose={() => setDialog(null)}
        busy={busy === 'completed'}
        fields={divergence != null && Math.abs(divergence) > 0
          ? [{ name: 'divergenceReason', label: lang === 'pt'
                ? `Motivo da divergência (${divergence > 0 ? 'falta' : 'sobra'} de ${Math.abs(divergence).toLocaleString('pt-PT')} L)`
                : `Divergence reason (${divergence > 0 ? 'short' : 'extra'} ${Math.abs(divergence).toLocaleString('en-GB')} L)`,
              placeholder: T('Ex.: perdas no descarregamento, calibração de bomba…', 'E.g.: spillage at unloading, pump calibration…'),
              multiline: true }]
          : []}
        onSubmit={(values) => postEvent('completed', values)}
      />

      {/* Visor de ampliação (lightbox) com ZOOM da foto da guia. Dialog em ecrã
          inteiro (portal → fica sempre por cima do popup do marco). Clicar na
          imagem aproxima no ponto clicado; clicar no fundo / ✕ fecha. */}
      <Dialog open={!!zoom} onClose={closeZoom} fullScreen
        PaperProps={{ style: { background: 'rgba(2,6,23,0.92)' } }}>
        {zoom && (
          <Box onClick={closeZoom}
            style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 20 }}>
            <Box onClick={e => e.stopPropagation()}
              style={{ overflow: 'hidden', maxWidth: '94vw', maxHeight: '84vh', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
              <img src={zoom.url} alt={zoom.label}
                onClick={(e) => {
                  if (zoomScale > 1) { setZoomScale(1); return; }
                  const r = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - r.left) / r.width) * 100;
                  const y = ((e.clientY - r.top) / r.height) * 100;
                  setZoomOrigin({ x, y });
                  setZoomScale(2.8);
                }}
                style={{ maxWidth: '94vw', maxHeight: '84vh', objectFit: 'contain', display: 'block',
                  transform: `scale(${zoomScale})`, transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                  transition: 'transform 0.15s ease', cursor: zoomScale > 1 ? 'zoom-out' : 'zoom-in' }} />
            </Box>
            <Typography style={{ color: '#fff', fontSize: '0.82rem', fontWeight: 700, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
              {zoom.label} · {zoomScale > 1
                ? T('clicar para reduzir', 'click to zoom out')
                : T('clicar na imagem para aproximar', 'click image to zoom in')}
            </Typography>
            <IconButton onClick={closeZoom}
              style={{ position: 'absolute', top: 14, right: 14, color: '#fff', background: 'rgba(255,255,255,0.14)' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}

// ── Sub-componente: dialog genérico com campos + foto ────────────────
function FuelEventDialog({ open, title, onClose, busy, fields = [], photoField, onSubmit, extraSection, validate, lang = 'pt' }) {
  const T = (pt, en) => (lang === 'pt' ? pt : en);
  const [values, setValues] = useState({});
  const [photo, setPhoto] = useState(null);
  const [valErr, setValErr] = useState(null);
  React.useEffect(() => {
    if (open) { setValues({}); setPhoto(null); setValErr(null); }
  }, [open]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4_000_000) { alert('Foto demasiado grande (limite 4 MB).'); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(f);
  };

  // Separar campos essenciais (com *) dos opcionais para grupos visuais.
  const essentialFields = fields.filter(f => f.label?.includes('*'));
  const optionalFields = fields.filter(f => !f.label?.includes('*'));

  const renderField = (f) => {
    const isMissing = valErr && f.label?.includes('*') && (() => {
      const v = values[f.name];
      if (v == null || String(v).trim() === '') return true;
      if (f.type === 'number' && !(Number(v) > 0)) return true;
      return false;
    })();
    return (
      <TextField key={f.name}
        size="small" variant="outlined" type={f.type || 'text'}
        label={f.label.replace(/\s*\*$/, '')} placeholder={f.placeholder}
        multiline={f.multiline} minRows={f.multiline ? 3 : undefined}
        value={values[f.name] || ''}
        onChange={e => setValues(v => ({ ...v, [f.name]: e.target.value }))}
        fullWidth
        required={f.label?.includes('*')}
        error={!!isMissing}
      />
    );
  };

  // Valida os campos obrigatórios (label com '*') antes de submeter. Campos
  // numéricos têm de ser > 0. Bloqueia o envio e mostra aviso inline em vez de
  // deixar o backend rejeitar (ou pior, gravar incompleto).
  const handleConfirm = () => {
    const missing = essentialFields.filter(f => {
      const v = values[f.name];
      if (v == null || String(v).trim() === '') return true;
      if (f.type === 'number' && !(Number(v) > 0)) return true;
      return false;
    });
    if (missing.length > 0) {
      const names = missing.map(f => f.label.replace(/\s*\*$/, '')).join(', ');
      setValErr(T(`Preenche os campos obrigatórios: ${names}`, `Fill in the required fields: ${names}`));
      return;
    }
    // Validação personalizada (ex.: selo + guia-ou-foto na entrega). Bloqueia e
    // mostra o aviso inline em vez de fechar sem marcar o marco.
    if (typeof validate === 'function') {
      const customErr = validate({ values, photo });
      if (customErr) { setValErr(customErr); return; }
    }
    setValErr(null);
    onSubmit({ ...values, ...(photoField && photo ? { [photoField]: photo } : {}) });
  };

  return (
    <Dialog open={open}
      onClose={(_e, reason) => {
        // Modal: não fechar por clique no backdrop nem ESC — evita perder o
        // que já foi preenchido. Só fecha via Cancelar / X.
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      disableBackdropClick disableEscapeKeyDown
      maxWidth="md" fullWidth
      PaperProps={{ style: { borderRadius: 14, overflow: 'hidden', maxHeight: '92vh' } }}>
      {/* Header polido com gradient soft + ícone em caixa */}
      <Box style={{
        padding: '16px 22px',
        background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 60%)',
        borderBottom: '1px solid #fed7aa',
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <Box style={{
          width: 38, height: 38, borderRadius: 10,
          background: '#fff', border: '1px solid #fed7aa', color: '#ea580c',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20,
        }}>⛽</Box>
        <Box style={{ flex: 1 }}>
          <Typography style={{ fontSize: '1.02rem', fontWeight: 800, color: '#9a3412', letterSpacing: '-0.005em' }}>
            {title}
          </Typography>
          <Typography style={{ fontSize: '0.62rem', fontWeight: 800, color: '#c2410c', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>
            {T('Move Express · Distribuição', 'Move Express · Distribution')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent style={{ padding: '20px 22px', background: '#fafafa', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Campos essenciais (obrigatórios *) */}
        {essentialFields.length > 0 && (
          <Box style={{
            background: '#fff', border: '1px solid #fed7aa', borderRadius: 12,
            padding: '14px 16px',
          }}>
            <Typography style={{
              fontSize: '0.62rem', fontWeight: 800, color: '#9a3412',
              textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10,
            }}>
              {T('Dados obrigatórios', 'Required data')}
            </Typography>
            <Box style={{ display: 'grid', gridTemplateColumns: essentialFields.length > 1 ? '1fr 1fr' : '1fr', gap: 10 }}>
              {essentialFields.map(renderField)}
            </Box>
          </Box>
        )}

        {/* Secção extra (Revelar PA real) */}
        {typeof extraSection === 'function' && extraSection({ values, setValues })}

        {/* Campos opcionais (facturação) — colapsável visualmente */}
        {optionalFields.length > 0 && (
          <Box style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
            padding: '14px 16px',
          }}>
            <Typography style={{
              fontSize: '0.62rem', fontWeight: 800, color: '#475569',
              textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {T('Facturação ao cliente', 'Customer invoicing')}
              <span style={{
                fontSize: '0.56rem', fontWeight: 700,
                background: '#f1f5f9', color: '#94a3b8',
                padding: '1px 6px', borderRadius: 999,
                letterSpacing: 0.4,
              }}>{T('OPCIONAL', 'OPTIONAL')}</span>
            </Typography>
            <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {optionalFields.map(renderField)}
            </Box>
          </Box>
        )}

        {/* Foto da guia */}
        {photoField && (
          <Box style={{
            background: '#fff', border: '1px solid #fed7aa', borderRadius: 12,
            padding: '14px 16px',
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <Typography style={{
                fontSize: '0.62rem', fontWeight: 800, color: '#9a3412',
                textTransform: 'uppercase', letterSpacing: 0.6,
              }}>
                {T('Foto da guia', 'Guide photo')}
              </Typography>
              {!photo && (
                <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  {T('recomendado — Robot IA preenche tudo sozinho em breve', 'recommended — AI Robot will fill everything in soon')}
                </Typography>
              )}
            </Box>
            <Box style={{
              padding: photo ? 8 : 20,
              border: `1.5px dashed ${photo ? '#bbf7d0' : '#fed7aa'}`,
              borderRadius: 10,
              background: photo ? '#f0fdf4' : '#fff7ed',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.12s',
            }}>
              <input type="file" accept="image/*" onChange={handleFile}
                style={{ display: 'none' }} id="fuel-photo-input" />
              <label htmlFor="fuel-photo-input" style={{ cursor: 'pointer', display: 'block' }}>
                {photo ? (
                  <Box>
                    <img src={photo} alt="Guia" style={{
                      maxWidth: '100%', maxHeight: 220, borderRadius: 6,
                      boxShadow: '0 2px 8px rgba(15,23,42,0.10)',
                    }} />
                    <Box style={{
                      marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 12px', borderRadius: 999,
                      background: '#dcfce7', color: '#15803d',
                      fontSize: '0.7rem', fontWeight: 800,
                    }}>
                      <CheckCircleIcon style={{ fontSize: 14 }} />
                      {T('Foto anexada · clica para mudar', 'Photo attached · click to change')}
                    </Box>
                  </Box>
                ) : (
                  <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <CloudUploadIcon style={{ fontSize: 40, color: '#fb923c' }} />
                    <Typography style={{ fontSize: '0.86rem', color: '#9a3412', fontWeight: 700 }}>
                      {T('Clica para anexar foto da guia', 'Click to attach guide photo')}
                    </Typography>
                    <Typography style={{ fontSize: '0.66rem', color: '#c2410c' }}>
                      {T('JPG, PNG até 4 MB', 'JPG, PNG up to 4 MB')}
                    </Typography>
                  </Box>
                )}
              </label>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{
        padding: '14px 22px', background: '#fff',
        borderTop: '1px solid #e2e8f0',
        display: 'flex', justifyContent: 'space-between',
      }}>
        <Typography style={{ fontSize: '0.66rem', color: valErr ? '#dc2626' : '#94a3b8', fontStyle: 'italic', fontWeight: valErr ? 700 : 400, maxWidth: 380 }}>
          {valErr ? `⚠ ${valErr}` : T('Campos com * são obrigatórios.', 'Fields marked * are required.')}
        </Typography>
        <Box style={{ display: 'flex', gap: 8 }}>
          <Button onClick={onClose} disabled={busy} style={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}>
            {T('Cancelar', 'Cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={busy}
            variant="contained"
            startIcon={busy ? null : <CheckCircleIcon style={{ fontSize: 18 }} />}
            style={{
              textTransform: 'none', fontWeight: 700,
              background: busy ? '#94a3b8' : 'linear-gradient(135deg, #fb923c, #f97316)',
              color: '#fff',
              padding: '7px 18px', borderRadius: 8,
              boxShadow: busy ? 'none' : '0 2px 6px rgba(249,115,22,0.28)',
            }}
          >
            {busy ? T('A guardar…', 'Saving…') : T('Confirmar', 'Confirm')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// Botão de ordenação A→Z / Z→A para os menus de favoritos (reutilizado nos 3
// diálogos de P.A.). A lista chega já ordenada ASC; este botão só inverte.
function FavSortBtn({ dir, onToggle }) {
  return (
    <Button size="small" onClick={onToggle}
      title={dir === 'asc' ? 'A → Z' : 'Z → A'}
      style={{ minWidth: 0, padding: '4px 8px', fontSize: '0.66rem', fontWeight: 800, color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 8, background: '#f0f9ff', textTransform: 'none', flexShrink: 0 }}>
      {dir === 'asc' ? 'A→Z' : 'Z→A'}
    </Button>
  );
}

// ─── RevealPABlock ──────────────────────────────────────────────────
// Bloco que aparece dentro do popup "Carregou" para o operador preencher o
// PA real (revelado pela Ordem Sonangol). Tem favoritos + geocoding.
function RevealPABlock({ values, setValues, favorites, lang = 'pt' }) {
  const T = (pt, en) => (lang === 'pt' ? pt : en);
  const [favAnchor, setFavAnchor] = useState(null);
  const [favQuery, setFavQuery] = useState('');
  const [favDir, setFavDir] = useState('asc');
  const closeFav = () => { setFavAnchor(null); setFavQuery(''); };
  const filteredFavsBase = (favorites || []).filter(f => {
    const q = favQuery.trim().toLowerCase();
    if (!q) return true;
    return String(f.name || '').toLowerCase().includes(q)
      || String(f.address || '').toLowerCase().includes(q);
  });
  // A lista base já vem ASC; para DESC basta inverter.
  const filteredFavs = favDir === 'desc' ? [...filteredFavsBase].reverse() : filteredFavsBase;
  const applyFav = (f) => {
    setValues(v => ({
      ...v,
      deliveryName: f.name || '',
      deliveryAddress: f.address || '',
      deliveryLat: f.latitude != null ? Number(f.latitude) : null,
      deliveryLng: f.longitude != null ? Number(f.longitude) : null,
    }));
    closeFav();
  };
  return (
    <Box style={{
      background: 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 60%)',
      border: '1px solid #fbbf24',
      borderRadius: 12,
      padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <Box style={{
          width: 32, height: 32, borderRadius: 8,
          background: '#fff', border: '1px solid #fbbf24', color: '#d97706',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, flexShrink: 0,
        }}>📍</Box>
        <Box style={{ flex: 1 }}>
          <Typography style={{
            fontSize: '0.66rem', fontWeight: 800, color: '#92400e',
            textTransform: 'uppercase', letterSpacing: 0.6,
          }}>
            {T('Revelar PA real', 'Reveal actual delivery point')}
          </Typography>
          <Typography style={{ fontSize: '0.72rem', color: '#78350f', marginTop: 2, lineHeight: 1.4 }}>
            {T(
              'Esta OT foi criada sem PA. A Ordem Sonangol traz o local exacto — escolhe dos favoritos ou preenche manualmente.',
              'This OT was created without a delivery point. The Sonangol Order contains the exact location — pick from favourites or fill in manually.'
            )}
          </Typography>
        </Box>
      </Box>

      {/* Botão Favoritos */}
      {favorites.length > 0 && (
        <Box>
          <Button
            size="small"
            startIcon={<StarIcon style={{ fontSize: 14 }} />}
            onClick={e => setFavAnchor(e.currentTarget)}
            style={{
              textTransform: 'none', fontSize: '0.72rem', fontWeight: 700,
              color: '#0369a1', background: '#f0f9ff',
              border: '1px solid #bae6fd', borderRadius: 8,
              padding: '5px 12px',
            }}
          >
            {T(`Escolher dos favoritos (${favorites.length})`, `Pick from favourites (${favorites.length})`)}
          </Button>
          <Menu
            anchorEl={favAnchor}
            open={!!favAnchor}
            onClose={closeFav}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ style: { maxHeight: 380, minWidth: 320 } }}
          >
            {/* Caixa de pesquisa + ordenação A→Z/Z→A. */}
            <Box style={{ padding: '6px 10px 8px', position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 6, alignItems: 'center' }}>
              <TextField
                autoFocus size="small" variant="outlined" style={{ flex: 1 }}
                placeholder={T('Pesquisar favorito…', 'Search favourite…')}
                value={favQuery}
                onChange={e => setFavQuery(e.target.value)}
                onKeyDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
              />
              <FavSortBtn dir={favDir} onToggle={() => setFavDir(d => d === 'asc' ? 'desc' : 'asc')} />
            </Box>
            {filteredFavs.length === 0 ? (
              <MenuItem disabled style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                {T('Sem resultados', 'No matches')}
              </MenuItem>
            ) : filteredFavs.map(f => (
              <MenuItem key={f.id} onClick={() => applyFav(f)} style={{ fontSize: '0.82rem' }}>
                <StarIcon style={{ fontSize: 14, color: '#f59e0b', marginRight: 8 }} />
                <Box>
                  <div style={{ fontWeight: 700 }}>{f.name}</div>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{f.address}</div>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}

      <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <TextField
          size="small" variant="outlined" fullWidth
          label={T('Nome do PA', 'Delivery point name')}
          placeholder="ex.: PA Coco Afonso S.Leão"
          value={values.deliveryName || ''}
          onChange={e => setValues(v => ({ ...v, deliveryName: e.target.value }))}
          style={{ background: '#fff', borderRadius: 6 }}
        />
        <AddressFieldShared
          label={T('Morada do PA', 'Delivery point address')}
          value={values.deliveryAddress || ''}
          onChange={txt => setValues(v => ({ ...v, deliveryAddress: txt }))}
          onSelect={geo => setValues(v => ({
            ...v,
            deliveryAddress: geo.name,
            deliveryLat: geo.coordinates?.[1] ?? null,
            deliveryLng: geo.coordinates?.[0] ?? null,
          }))}
          country="AO"
          helperText={T('Escreve 3+ letras para sugestões com GPS', 'Type 3+ letters for GPS suggestions')}
        />
      </Box>
      {values.deliveryLat && values.deliveryLng && (
        <Box style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: '0.7rem', color: '#15803d', fontWeight: 700,
          background: '#dcfce7', border: '1px solid #86efac',
          padding: '5px 12px', borderRadius: 999,
          alignSelf: 'flex-start',
        }}>
          <CheckCircleIcon style={{ fontSize: 14 }} />
          {T('GPS guardado', 'GPS saved')}: {Number(values.deliveryLat).toFixed(5)}, {Number(values.deliveryLng).toFixed(5)}
        </Box>
      )}
    </Box>
  );
}

export default FuelOTPanel;
